"use client";
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cva } from 'class-variance-authority';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { Slot } from '@radix-ui/react-slot';
import { X, PanelLeft, Minus, ArrowDown, ArrowUp, Shield, Settings, LogOut, Zap, FileText, Play, MessageSquare, Code, AlertCircle, CheckCircle, Info, Clock, Loader2, FolderOpen, Pencil, Terminal, ChevronDown, ChevronRight, User, Bot, Camera, Trash2, Check, Activity, Filter, RefreshCw, Paperclip, Send, Bell, CheckCheck, Folder, MoreHorizontal, Users, Calendar, ExternalLink, XCircle, ChevronUp, ArrowUpDown, ChevronLeft, MinusIcon, CheckIcon, CircleIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, FileQuestion, Search, Inbox, Square, Undo2, MousePointerClick, Smartphone, Tablet, Monitor, CheckCircle2, Circle, GripVertical, Pause, ArrowLeft, RotateCcw, Save } from 'lucide-react';
import * as React17 from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as LabelPrimitive from '@radix-ui/react-label';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Toaster as Toaster$1 } from 'sonner';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import '@xterm/xterm/css/xterm.css';

// src/lib/utils.ts
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/tokens/index.ts
var colors = {
  /** Base/Background colors - Navy foundation */
  base: "#030c30",
  surface1: "#0d1638",
  surface2: "#171f41",
  surface3: "#242c4b",
  /** Primary brand color - Electric Blue (7.2:1 contrast with white text) */
  primary: "#232eff",
  primaryHover: "#4d55ff",
  primaryLight: "#898fff",
  primaryDark: "#0009bc",
  primary5: "rgba(35, 46, 255, 0.05)",
  primary10: "rgba(35, 46, 255, 0.1)",
  primary15: "rgba(35, 46, 255, 0.15)",
  primary20: "rgba(35, 46, 255, 0.2)",
  primary25: "rgba(35, 46, 255, 0.25)",
  primary30: "rgba(35, 46, 255, 0.3)",
  primary35: "rgba(35, 46, 255, 0.35)",
  primary50: "rgba(35, 46, 255, 0.5)",
  primary55: "rgba(35, 46, 255, 0.55)",
  primary75: "rgba(35, 46, 255, 0.75)",
  primary100: "#232eff",
  /** Accent - Brand Orange (for brand moments, not primary actions) */
  accentOrange: "#c93b1d",
  accentOrangeHover: "#f26649",
  accentOrange10: "rgba(201, 59, 29, 0.1)",
  accentOrange20: "rgba(201, 59, 29, 0.2)",
  accentOrange50: "rgba(201, 59, 29, 0.5)",
  /** Semantic colors (brand values) */
  success: "#16a34a",
  successHover: "#15803d",
  warning: "#ca8a04",
  warningHover: "#a16207",
  error: "#dc2626",
  errorHover: "#b91c1c",
  info: "#0284c7",
  infoHover: "#0369a1",
  /** Text colors */
  textPrimary: "#ffffff",
  textSecondary: "#a3a3a3",
  textMuted: "#7a7a85",
  textInverse: "#030c30",
  /** Border colors */
  border: "#303855",
  borderMuted: "#1c2445"
};
var spacing = {
  0: "0",
  px: "1px",
  0.5: "0.125rem",
  1: "0.25rem",
  // 4px
  1.5: "0.375rem",
  2: "0.5rem",
  // 8px
  2.5: "0.625rem",
  3: "0.75rem",
  // 12px
  3.5: "0.875rem",
  4: "1rem",
  // 16px
  5: "1.25rem",
  // 20px
  6: "1.5rem",
  // 24px
  7: "1.75rem",
  8: "2rem",
  // 32px
  9: "2.25rem",
  10: "2.5rem",
  // 40px
  12: "3rem",
  // 48px
  14: "3.5rem",
  16: "4rem",
  // 64px
  20: "5rem",
  24: "6rem"
};
var radius = {
  none: "0",
  xs: "2px",
  sm: "4px",
  // inputs
  md: "6px",
  // buttons
  lg: "0.5rem",
  // 8px - cards
  xl: "12px",
  "2xl": "1rem",
  // 16px - modals
  "3xl": "1.5rem",
  // 24px
  full: "9999px"
  // badges, pills
};
var transitions = {
  fast: "150ms",
  normal: "300ms",
  slow: "500ms"
};
var zIndex = {
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  modalBackdrop: 400,
  modal: 500,
  popover: 600,
  tooltip: 700,
  toast: 800
};
var breakpoints = {
  xs: "475px",
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px"
};
var layout = {
  leftRailWidth: "240px",
  topNavHeight: "52px",
  bottomTabHeight: "56px"
};
var workflowStages = {
  create: { intensity: "15%", color: colors.primary15 },
  interview: { intensity: "35%", color: colors.primary35 },
  develop: { intensity: "55%", color: colors.primary55 },
  review: { intensity: "75%", color: colors.primary75 },
  finalize: { intensity: "100%", color: colors.primary100 },
  closed: { intensity: "muted", color: colors.surface2 }
};
var alertVariants = cva(
  "relative w-full rounded-lg border p-4 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:size-4 [&:has(svg)]:pl-11",
  {
    variants: {
      variant: {
        default: "bg-surface-1 text-foreground border-border",
        destructive: "bg-destructive/10 text-destructive border-destructive [&>svg]:text-destructive",
        success: "bg-success/10 text-success border-success [&>svg]:text-success",
        warning: "bg-warning/10 text-warning border-warning [&>svg]:text-warning"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Alert({ className, variant = "default", ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "alert",
      "data-variant": variant,
      role: "alert",
      className: cn(alertVariants({ variant, className })),
      ...props
    }
  );
}
function AlertTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "h5",
    {
      "data-slot": "alert-title",
      className: cn("leading-none font-medium tracking-tight", className),
      ...props
    }
  );
}
function AlertDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "alert-description",
      className: cn("text-sm [&_p]:leading-relaxed", className),
      ...props
    }
  );
}
function AvatarRoot({
  className,
  size = "default",
  ...props
}) {
  const resolvedSize = size === "md" ? "default" : size;
  return /* @__PURE__ */ jsx(
    AvatarPrimitive.Root,
    {
      "data-slot": "avatar",
      "data-size": resolvedSize,
      className: cn(
        "group/avatar relative flex size-8 shrink-0 overflow-hidden rounded-full select-none data-[size=lg]:size-10 data-[size=sm]:size-6",
        className
      ),
      ...props
    }
  );
}
function AvatarImage({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    AvatarPrimitive.Image,
    {
      "data-slot": "avatar-image",
      className: cn("aspect-square size-full", className),
      ...props
    }
  );
}
function AvatarFallback({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    AvatarPrimitive.Fallback,
    {
      "data-slot": "avatar-fallback",
      className: cn(
        "bg-muted text-muted-foreground flex size-full items-center justify-center rounded-full text-sm group-data-[size=sm]/avatar:text-xs",
        className
      ),
      ...props
    }
  );
}
function getInitials(name) {
  const parts = name.trim().split(/\s+/);
  const firstPart = parts[0] ?? "";
  const lastPart = parts[parts.length - 1] ?? "";
  if (parts.length === 1 || firstPart === lastPart) {
    return firstPart.slice(0, 2).toUpperCase();
  }
  const firstInitial = firstPart[0] ?? "";
  const lastInitial = lastPart[0] ?? "";
  return (firstInitial + lastInitial).toUpperCase();
}
var statusColors = {
  online: "bg-green-500",
  offline: "bg-muted-foreground",
  busy: "bg-destructive",
  away: "bg-yellow-500"
};
function Avatar({
  className,
  size: sizeProp = "default",
  src,
  alt,
  fallback,
  status,
  ...props
}) {
  const size = sizeProp === "md" ? "default" : sizeProp;
  const initials = fallback ? getInitials(fallback) : "?";
  const altText = alt ?? fallback ?? "User avatar";
  return /* @__PURE__ */ jsxs(AvatarRoot, { size, className, ...props, children: [
    /* @__PURE__ */ jsx(AvatarImage, { src, alt: altText }),
    /* @__PURE__ */ jsx(AvatarFallback, { delayMs: 600, children: initials }),
    status && /* @__PURE__ */ jsx(
      "span",
      {
        "data-slot": "avatar-badge",
        role: "img",
        className: cn(
          "ring-background absolute right-0 bottom-0 z-10 rounded-full ring-2",
          statusColors[status],
          size === "sm" && "size-1.5",
          size === "default" && "size-2",
          size === "lg" && "size-2.5"
        ),
        "aria-label": `Status: ${status}`
      }
    )
  ] });
}
var badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border border-transparent px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none transition-[color,box-shadow] overflow-hidden focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive: "bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: "text-foreground border-border [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        // Cortex custom variants
        success: "bg-success/10 text-success border-success/20",
        warning: "bg-warning/10 text-warning border-warning/20",
        info: "bg-info/10 text-info border-info/20"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, asChild = false, ...props }) {
  const Comp = asChild ? Slot : "span";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      "data-slot": "badge",
      "data-variant": variant,
      className: cn(badgeVariants({ variant }), className),
      ...props
    }
  );
}
var Breadcrumb = React17.forwardRef(({ ...props }, ref) => /* @__PURE__ */ jsx("nav", { ref, "aria-label": "breadcrumb", ...props }));
Breadcrumb.displayName = "Breadcrumb";
var BreadcrumbList = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "ol",
  {
    ref,
    className: cn(
      "text-foreground/60 flex flex-wrap items-center gap-1.5 text-sm break-words",
      className
    ),
    ...props
  }
));
BreadcrumbList.displayName = "BreadcrumbList";
var BreadcrumbItem = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "li",
  {
    ref,
    className: cn("inline-flex items-center gap-1.5", className),
    ...props
  }
));
BreadcrumbItem.displayName = "BreadcrumbItem";
var BreadcrumbLink = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "a",
  {
    ref,
    className: cn(
      "text-foreground/60 hover:text-foreground focus-visible:ring-primary rounded transition-colors focus-visible:ring-2 focus-visible:outline-none",
      className
    ),
    ...props
  }
));
BreadcrumbLink.displayName = "BreadcrumbLink";
var BreadcrumbPage = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "span",
  {
    ref,
    role: "link",
    "aria-disabled": "true",
    "aria-current": "page",
    className: cn("text-foreground font-medium", className),
    ...props
  }
));
BreadcrumbPage.displayName = "BreadcrumbPage";
var BreadcrumbSeparator = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "li",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("[&>svg]:h-3.5 [&>svg]:w-3.5", className),
    ...props,
    children: children ?? /* @__PURE__ */ jsx(ChevronRight, { className: "text-foreground/60" })
  }
);
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";
var BreadcrumbEllipsis = ({
  className,
  ...props
}) => /* @__PURE__ */ jsxs(
  "span",
  {
    role: "presentation",
    "aria-hidden": "true",
    className: cn("flex h-9 w-9 items-center justify-center", className),
    ...props,
    children: [
      /* @__PURE__ */ jsx(MoreHorizontal, { className: "text-foreground/60 h-4 w-4" }),
      /* @__PURE__ */ jsx("span", { className: "sr-only", children: "More" })
    ]
  }
);
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis";
var spinnerVariants = cva(
  "inline-block animate-spin rounded-full border-[1.5px] border-solid",
  {
    variants: {
      size: {
        xs: "h-3 w-3",
        sm: "h-4 w-4",
        md: "h-5 w-5",
        lg: "h-6 w-6"
      },
      variant: {
        default: "border-primary border-t-transparent",
        light: "border-white border-t-transparent",
        muted: "border-muted-foreground border-t-transparent"
      }
    },
    defaultVariants: {
      size: "md",
      variant: "default"
    }
  }
);
var Spinner = React17.forwardRef(
  ({ className, size, variant, label = "Loading", ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "span",
      {
        role: "status",
        "aria-label": label,
        className: cn(spinnerVariants({ size, variant, className })),
        ref,
        ...props,
        children: /* @__PURE__ */ jsx("span", { className: "sr-only", children: label })
      }
    );
  }
);
Spinner.displayName = "Spinner";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive: "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        md: "h-9 px-4 py-2 has-[>svg]:px-3",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  loading = false,
  leftAddon,
  rightAddon,
  disabled,
  children,
  type = "button",
  ...props
}) {
  const Comp = asChild ? Slot : "button";
  const isDisabled = disabled || loading;
  return /* @__PURE__ */ jsx(
    Comp,
    {
      "data-slot": "button",
      "data-variant": variant,
      "data-size": size,
      className: cn(buttonVariants({ variant, size, className })),
      disabled: isDisabled,
      "aria-busy": loading || void 0,
      type: !asChild ? type : void 0,
      ...props,
      children: loading ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2" }),
        children
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        leftAddon,
        children,
        rightAddon
      ] })
    }
  );
}
function Card({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card",
      className: cn(
        "border-border bg-surface-1 text-card-foreground rounded-xl border shadow-sm",
        className
      ),
      ...props
    }
  );
}
function CardHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-header",
      className: cn("flex flex-col gap-1.5 p-6", className),
      ...props
    }
  );
}
function CardTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-title",
      className: cn(
        "text-foreground leading-none font-medium tracking-tight",
        className
      ),
      ...props
    }
  );
}
function CardDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-description",
      className: cn("text-foreground/60 text-sm", className),
      ...props
    }
  );
}
function CardContent({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-content",
      className: cn("p-6 pt-0", className),
      ...props
    }
  );
}
function CardFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "card-footer",
      className: cn("flex items-center p-6 pt-0", className),
      ...props
    }
  );
}
var Center = React17.forwardRef(
  ({ className, minHeight, style, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        "data-slot": "center",
        className: cn("flex items-center justify-center", className),
        style: minHeight ? { minHeight, ...style } : style,
        ...props
      }
    );
  }
);
Center.displayName = "Center";
function Checkbox({
  className,
  indeterminate,
  checked,
  ...props
}) {
  const isIndeterminate = indeterminate && checked === void 0;
  const checkedValue = isIndeterminate ? "indeterminate" : checked;
  return /* @__PURE__ */ jsx(
    CheckboxPrimitive.Root,
    {
      "data-slot": "checkbox",
      className: cn(
        "peer border-input dark:bg-input/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary",
        className
      ),
      ...checkedValue !== void 0 ? { checked: checkedValue } : {},
      ...props,
      children: /* @__PURE__ */ jsx(
        CheckboxPrimitive.Indicator,
        {
          "data-slot": "checkbox-indicator",
          className: "grid place-content-center text-current transition-none",
          children: isIndeterminate || checkedValue === "indeterminate" ? /* @__PURE__ */ jsx(MinusIcon, { className: "size-3.5" }) : /* @__PURE__ */ jsx(CheckIcon, { className: "size-3.5" })
        }
      )
    }
  );
}
var containerVariants = cva("mx-auto w-full", {
  variants: {
    maxWidth: {
      sm: "max-w-sm",
      md: "max-w-md",
      lg: "max-w-lg",
      xl: "max-w-xl",
      "2xl": "max-w-2xl",
      "3xl": "max-w-3xl",
      "4xl": "max-w-4xl",
      "5xl": "max-w-5xl",
      "6xl": "max-w-6xl",
      "7xl": "max-w-7xl",
      full: "max-w-full"
    },
    padding: {
      true: "px-4 lg:px-8",
      false: ""
    }
  },
  defaultVariants: {
    maxWidth: "7xl",
    padding: true
  }
});
var Container = React17.forwardRef(
  ({ className, maxWidth, padding, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        "data-slot": "container",
        className: cn(containerVariants({ maxWidth, padding }), className),
        ...props
      }
    );
  }
);
Container.displayName = "Container";
var Dialog = DialogPrimitive.Root;
var DialogTrigger = DialogPrimitive.Trigger;
var DialogPortal = DialogPrimitive.Portal;
var DialogClose = DialogPrimitive.Close;
var dialogOverlayVariants = cva(
  "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  {
    variants: {},
    defaultVariants: {}
  }
);
var DialogOverlay = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(dialogOverlayVariants(), className),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
var dialogContentVariants = cva(
  [
    "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
    "grid gap-4",
    "rounded-xl border border-border bg-surface-2 shadow-xl",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
    "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
    "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
    "duration-200"
  ].join(" "),
  {
    variants: {
      size: {
        sm: "w-full max-w-sm",
        md: "w-full max-w-md",
        lg: "w-full max-w-lg",
        xl: "w-full max-w-xl",
        full: "w-full max-w-[calc(100vw-4rem)] h-[calc(100vh-4rem)]"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var DialogContent = React17.forwardRef(({ className, children, size, showCloseButton = true, ...props }, ref) => /* @__PURE__ */ jsxs(DialogPortal, { children: [
  /* @__PURE__ */ jsx(DialogOverlay, {}),
  /* @__PURE__ */ jsxs(
    DialogPrimitive.Content,
    {
      ref,
      className: cn(dialogContentVariants({ size }), className),
      ...props,
      children: [
        children,
        showCloseButton && /* @__PURE__ */ jsxs(DialogPrimitive.Close, { className: "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-foreground/60 absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none", children: [
          /* @__PURE__ */ jsx(X, { className: "h-4 w-4" }),
          /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
        ] })
      ]
    }
  )
] }));
DialogContent.displayName = DialogPrimitive.Content.displayName;
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "border-border flex flex-col gap-1.5 border-b px-6 py-4 text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "border-border flex flex-col-reverse gap-2 border-t px-6 py-4 sm:flex-row sm:justify-end",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";
var DialogTitle = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn(
      "text-foreground text-lg leading-none font-medium tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
var DialogDescription = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-foreground/60 text-sm", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
var DialogBody = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx("div", { className: cn("overflow-y-auto px-6", className), ...props });
DialogBody.displayName = "DialogBody";
function Separator({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    SeparatorPrimitive.Root,
    {
      "data-slot": "separator",
      decorative,
      orientation,
      className: cn(
        "bg-border shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px",
        className
      ),
      ...props
    }
  );
}
var Divider = Separator;
function DropdownMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Root, { "data-slot": "dropdown-menu", ...props });
}
function DropdownMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { "data-slot": "dropdown-menu-portal", ...props });
}
function DropdownMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Trigger,
    {
      "data-slot": "dropdown-menu-trigger",
      ...props
    }
  );
}
function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Content,
    {
      "data-slot": "dropdown-menu-content",
      sideOffset,
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md",
        className
      ),
      ...props
    }
  ) });
}
function DropdownMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Group, { "data-slot": "dropdown-menu-group", ...props });
}
function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Item,
    {
      "data-slot": "dropdown-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuCheckboxItem({
  className,
  children,
  checked = false,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    DropdownMenuPrimitive.CheckboxItem,
    {
      "data-slot": "dropdown-menu-checkbox-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked: checked ?? false,
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-4" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.RadioGroup,
    {
      "data-slot": "dropdown-menu-radio-group",
      ...props
    }
  );
}
function DropdownMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    DropdownMenuPrimitive.RadioItem,
    {
      "data-slot": "dropdown-menu-radio-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CircleIcon, { className: "size-2 fill-current" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Label,
    {
      "data-slot": "dropdown-menu-label",
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      className: cn("bg-border -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function DropdownMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      "data-slot": "dropdown-menu-shortcut",
      className: cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Sub, { "data-slot": "dropdown-menu-sub", ...props });
}
function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    DropdownMenuPrimitive.SubTrigger,
    {
      "data-slot": "dropdown-menu-sub-trigger",
      "data-inset": inset,
      className: cn(
        "focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(ChevronRightIcon, { className: "ml-auto size-4" })
      ]
    }
  );
}
function DropdownMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DropdownMenuPrimitive.SubContent,
    {
      "data-slot": "dropdown-menu-sub-content",
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-lg",
        className
      ),
      ...props
    }
  );
}
var formVariants = cva("flex flex-col", {
  variants: {
    gap: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
      xl: "gap-8"
    }
  },
  defaultVariants: {
    gap: "lg"
  }
});
var Form = React17.forwardRef(
  ({ className, gap, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "form",
      {
        ref,
        "data-slot": "form",
        className: cn(formVariants({ gap }), className),
        ...props
      }
    );
  }
);
Form.displayName = "Form";
var gridVariants = cva("grid", {
  variants: {
    gap: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
      xl: "gap-8"
    },
    columns: {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
      5: "grid-cols-5",
      6: "grid-cols-6"
    }
  },
  defaultVariants: {
    gap: "md",
    columns: 1
  }
});
var Grid = React17.forwardRef(
  ({ className, gap, columns, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        "data-slot": "grid",
        className: cn(gridVariants({ gap, columns }), className),
        ...props
      }
    );
  }
);
Grid.displayName = "Grid";
var gridItemVariants = cva("", {
  variants: {
    span: {
      1: "col-span-1",
      2: "col-span-2",
      3: "col-span-3",
      4: "col-span-4",
      5: "col-span-5",
      6: "col-span-6"
    }
  }
});
var GridItem = React17.forwardRef(
  ({ className, span, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        "data-slot": "grid-item",
        className: cn(gridItemVariants({ span }), className),
        ...props
      }
    );
  }
);
GridItem.displayName = "GridItem";
var headingVariants = cva("tracking-tight", {
  variants: {
    level: {
      1: "text-4xl font-semibold leading-display",
      2: "text-3xl font-semibold leading-heading",
      3: "text-2xl font-medium leading-title",
      4: "text-xl font-medium leading-subtitle",
      5: "text-lg font-medium leading-body",
      6: "text-base font-medium leading-prose"
    },
    color: {
      default: "text-foreground",
      secondary: "text-foreground/60",
      muted: "text-foreground/60 opacity-70",
      primary: "text-primary",
      inherit: "text-inherit"
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right"
    }
  },
  defaultVariants: {
    level: 1,
    color: "default",
    align: "left"
  }
});
var Heading = React17.forwardRef(
  ({ className, level = 1, color, align, children, ...props }, ref) => {
    const Tag = `h${level}`;
    return /* @__PURE__ */ jsx(
      Tag,
      {
        ref,
        className: cn(headingVariants({ level, color, align, className })),
        ...props,
        children
      }
    );
  }
);
Heading.displayName = "Heading";
var Image3 = React17.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx("img", { ref, "data-slot": "image", className: cn(className), ...props });
  }
);
Image3.displayName = "Image";
var inlineVariants = cva("flex flex-row", {
  variants: {
    gap: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
      xl: "gap-8"
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      baseline: "items-baseline",
      stretch: "items-stretch"
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between"
    },
    wrap: {
      true: "flex-wrap",
      false: ""
    }
  },
  defaultVariants: {
    gap: "sm",
    align: "center"
  }
});
var Inline = React17.forwardRef(
  ({ className, gap, align, justify, wrap, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        "data-slot": "inline",
        className: cn(inlineVariants({ gap, align, justify, wrap }), className),
        ...props
      }
    );
  }
);
Inline.displayName = "Inline";
var inputClasses = [
  "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground",
  "dark:bg-input/30 border-input",
  "flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs",
  "transition-[color,box-shadow] outline-none",
  "file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium",
  "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  "md:text-sm",
  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
].join(" ");
function Input({
  className,
  type,
  error,
  errorMessage,
  leftAddon,
  rightAddon,
  id,
  ...props
}) {
  const errorId = React17.useId();
  const ariaDescribedBy = errorMessage ? errorId : props["aria-describedby"];
  const hasAddons = leftAddon || rightAddon;
  if (hasAddons) {
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "dark:bg-input/30 relative flex h-9 items-center rounded-md border bg-transparent shadow-xs transition-[color,box-shadow]",
            "focus-within:ring-ring/50 focus-within:ring-[3px] focus-within:outline-none",
            error ? "border-destructive focus-within:border-destructive focus-within:ring-destructive/20" : "border-input focus-within:border-ring",
            props.disabled && "cursor-not-allowed opacity-50"
          ),
          children: [
            leftAddon && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground absolute left-3 flex items-center", children: leftAddon }),
            /* @__PURE__ */ jsx(
              "input",
              {
                "data-slot": "input",
                id,
                type,
                className: cn(
                  "text-foreground placeholder:text-muted-foreground flex h-full w-full bg-transparent px-3 py-1 text-base outline-none disabled:cursor-not-allowed md:text-sm",
                  leftAddon && "pl-9",
                  rightAddon && "pr-9",
                  className
                ),
                "aria-invalid": error || void 0,
                "aria-describedby": ariaDescribedBy,
                ...props
              }
            ),
            rightAddon && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground absolute right-3 flex items-center", children: rightAddon })
          ]
        }
      ),
      errorMessage && /* @__PURE__ */ jsx(
        "p",
        {
          id: errorId,
          className: "text-destructive mt-1.5 text-sm",
          role: "alert",
          children: errorMessage
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        "data-slot": "input",
        id,
        type,
        className: cn(inputClasses, className),
        "aria-invalid": error || void 0,
        "aria-describedby": ariaDescribedBy,
        ...props
      }
    ),
    errorMessage && /* @__PURE__ */ jsx(
      "p",
      {
        id: errorId,
        className: "text-destructive mt-1.5 text-sm",
        role: "alert",
        children: errorMessage
      }
    )
  ] });
}
var labelVariants = cva(
  "flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "text-foreground",
        error: "text-destructive",
        muted: "text-muted-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Label2({
  className,
  variant,
  required,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    LabelPrimitive.Root,
    {
      "data-slot": "label",
      className: cn(labelVariants({ variant, className })),
      ...props,
      children: [
        children,
        required && /* @__PURE__ */ jsx("span", { className: "text-destructive ml-1", "aria-label": "required", children: "*" })
      ]
    }
  );
}
var listVariants = cva("flex flex-col", {
  variants: {
    gap: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6"
    },
    divided: {
      true: "divide-border divide-y gap-0",
      false: ""
    }
  },
  defaultVariants: {
    gap: "sm"
  }
});
var List = React17.forwardRef(
  ({ className, gap, divided, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "ul",
      {
        ref,
        "data-slot": "list",
        className: cn(listVariants({ gap, divided }), className),
        ...props
      }
    );
  }
);
List.displayName = "List";
var ListItem = React17.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "li",
      {
        ref,
        "data-slot": "list-item",
        className: cn(className),
        ...props
      }
    );
  }
);
ListItem.displayName = "ListItem";
var stackVariants = cva("flex flex-col", {
  variants: {
    gap: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-4",
      lg: "gap-6",
      xl: "gap-8",
      "2xl": "gap-12"
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      stretch: "items-stretch"
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between"
    }
  },
  defaultVariants: {
    gap: "md",
    align: "stretch"
  }
});
var Stack = React17.forwardRef(
  ({ className, gap, align, justify, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        "data-slot": "stack",
        className: cn(stackVariants({ gap, align, justify }), className),
        ...props
      }
    );
  }
);
Stack.displayName = "Stack";
var textVariants = cva("", {
  variants: {
    size: {
      xs: "text-sm leading-body",
      sm: "text-base leading-body",
      base: "text-base leading-prose",
      lg: "text-lg leading-body",
      xl: "text-xl leading-subtitle",
      "2xl": "text-2xl leading-title",
      "3xl": "text-3xl leading-heading",
      "4xl": "text-4xl leading-display"
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold"
    },
    color: {
      default: "text-foreground",
      secondary: "text-foreground/70",
      muted: "text-foreground/60",
      inverse: "text-background",
      primary: "text-primary",
      success: "text-success",
      warning: "text-warning",
      error: "text-error",
      info: "text-info",
      inherit: "text-inherit"
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right"
    },
    truncate: {
      true: "truncate",
      false: ""
    }
  },
  defaultVariants: {
    size: "base",
    weight: "normal",
    color: "default",
    align: "left",
    truncate: false
  }
});
var Text = React17.forwardRef(
  ({
    className,
    size,
    weight,
    color,
    align,
    truncate,
    as: Component = "p",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref,
        className: cn(
          textVariants({ size, weight, color, align, truncate, className })
        ),
        ...props
      }
    );
  }
);
Text.displayName = "Text";
var PageHeader = React17.forwardRef(
  ({ className, title, subtitle, actions, icon, badge, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        "data-slot": "page-header",
        className: cn(className),
        ...props,
        children: /* @__PURE__ */ jsxs(
          Inline,
          {
            justify: "between",
            align: "start",
            gap: "md",
            className: "flex-col gap-4 sm:flex-row sm:items-center",
            children: [
              /* @__PURE__ */ jsxs(Stack, { gap: "xs", children: [
                /* @__PURE__ */ jsxs(Inline, { gap: "sm", children: [
                  icon,
                  /* @__PURE__ */ jsx(Heading, { level: 1, className: "text-2xl font-bold", children: title }),
                  badge
                ] }),
                subtitle && /* @__PURE__ */ jsx(Text, { color: "secondary", size: "base", children: subtitle })
              ] }),
              actions && /* @__PURE__ */ jsx(Inline, { gap: "sm", className: "shrink-0", children: actions })
            ]
          }
        )
      }
    );
  }
);
PageHeader.displayName = "PageHeader";
var indicatorColors = {
  default: "bg-primary",
  success: "bg-green-500",
  warning: "bg-yellow-500",
  error: "bg-destructive"
};
function Progress({
  className,
  value,
  variant = "default",
  indeterminate = false,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    ProgressPrimitive.Root,
    {
      "data-slot": "progress",
      className: cn(
        "bg-primary/20 relative h-2 w-full overflow-hidden rounded-full",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        ProgressPrimitive.Indicator,
        {
          "data-slot": "progress-indicator",
          className: cn(
            "h-full w-full flex-1 transition-all",
            indicatorColors[variant],
            indeterminate && "animate-progress-indeterminate"
          ),
          style: indeterminate ? void 0 : { transform: `translateX(-${100 - (value ?? 0)}%)` }
        }
      )
    }
  );
}
function RadioGroup2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Root,
    {
      "data-slot": "radio-group",
      className: cn("grid gap-3", className),
      ...props
    }
  );
}
function RadioGroupItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      "data-slot": "radio-group-item",
      className: cn(
        "border-input text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "relative flex items-center justify-center", children: /* @__PURE__ */ jsx(CircleIcon, { className: "size-2 fill-current" }) })
    }
  );
}
var Section = React17.forwardRef(
  ({ className, title, titleId, titleAction, children, ...props }, ref) => {
    const id = titleId ?? (title ? `section-${title.toLowerCase().replace(/\s+/g, "-")}` : void 0);
    return /* @__PURE__ */ jsx(
      "section",
      {
        ref,
        "data-slot": "section",
        "aria-labelledby": id,
        className: cn(className),
        ...props,
        children: /* @__PURE__ */ jsxs(Stack, { gap: "md", children: [
          title && /* @__PURE__ */ jsxs(Inline, { justify: "between", children: [
            /* @__PURE__ */ jsx(Heading, { level: 2, id, className: "text-lg font-semibold", children: title }),
            titleAction
          ] }),
          children
        ] })
      }
    );
  }
);
Section.displayName = "Section";
var Select = SelectPrimitive.Root;
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Trigger,
    {
      "data-slot": "select-trigger",
      "data-size": size,
      className: cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-4 opacity-50" }) })
      ]
    }
  );
}
function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}) {
  return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
    SelectPrimitive.Content,
    {
      "data-slot": "select-content",
      className: cn(
        "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border shadow-md",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      ...props,
      children: [
        /* @__PURE__ */ jsx(SelectScrollUpButton, {}),
        /* @__PURE__ */ jsx(
          SelectPrimitive.Viewport,
          {
            className: cn(
              "p-1",
              position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
            ),
            children
          }
        ),
        /* @__PURE__ */ jsx(SelectScrollDownButton, {})
      ]
    }
  ) });
}
function SelectItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Item,
    {
      "data-slot": "select-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "absolute right-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-4" }) }) }),
        /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
      ]
    }
  );
}
function SelectLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    SelectPrimitive.Label,
    {
      "data-slot": "select-label",
      className: cn(
        "text-muted-foreground px-2 py-1.5 text-sm font-medium",
        className
      ),
      ...props
    }
  );
}
function SelectSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    SelectPrimitive.Separator,
    {
      "data-slot": "select-separator",
      className: cn("bg-border -mx-1 my-1 h-px", className),
      ...props
    }
  );
}
function SelectScrollUpButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    SelectPrimitive.ScrollUpButton,
    {
      "data-slot": "select-scroll-up-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(ChevronUpIcon, { className: "size-4" })
    }
  );
}
function SelectScrollDownButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    SelectPrimitive.ScrollDownButton,
    {
      "data-slot": "select-scroll-down-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-4" })
    }
  );
}
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const listener = (event) => {
      setMatches(event.matches);
    };
    media.addEventListener("change", listener);
    return () => {
      media.removeEventListener("change", listener);
    };
  }, [query]);
  return matches;
}
var breakpoints2 = {
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)"};
function useIsMobile() {
  return !useMediaQuery(breakpoints2.md);
}
function useIsDesktop() {
  return useMediaQuery(breakpoints2.lg);
}
var SIDEBAR_WIDTH = "15rem";
var SIDEBAR_WIDTH_COLLAPSED = "3rem";
var SidebarContext = React17.createContext(null);
function useSidebar() {
  const context = React17.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
var SidebarProvider = React17.forwardRef(
  ({
    defaultOpen = true,
    open: openProp,
    onOpenChange: setOpenProp,
    className,
    children,
    style,
    ...props
  }, ref) => {
    const [_open, _setOpen] = React17.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React17.useCallback(
      (value) => {
        const openState = typeof value === "function" ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }
      },
      [setOpenProp, open]
    );
    const toggleSidebar = React17.useCallback(() => {
      setOpen((prev) => !prev);
    }, [setOpen]);
    const contextValue = React17.useMemo(
      () => ({ open, setOpen, toggleSidebar }),
      [open, setOpen, toggleSidebar]
    );
    return /* @__PURE__ */ jsx(SidebarContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        style: {
          "--sidebar-width": SIDEBAR_WIDTH,
          "--sidebar-width-collapsed": SIDEBAR_WIDTH_COLLAPSED,
          ...style
        },
        className: cn("flex h-full w-full", className),
        ...props,
        children
      }
    ) });
  }
);
SidebarProvider.displayName = "SidebarProvider";
var Sidebar = React17.forwardRef(
  ({ side = "left", collapsible = "icon", className, children, ...props }, ref) => {
    const { open, setOpen } = useSidebar();
    const isMobile = useIsMobile();
    if (collapsible === "none") {
      return /* @__PURE__ */ jsx(
        "aside",
        {
          ref,
          className: cn(
            "bg-background border-border flex h-full w-[var(--sidebar-width)] flex-col border-r",
            className
          ),
          ...props,
          children
        }
      );
    }
    if (isMobile) {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        open && /* @__PURE__ */ jsx(
          "div",
          {
            className: "fixed inset-0 z-40 bg-black/50",
            onClick: () => setOpen(false)
          }
        ),
        /* @__PURE__ */ jsx(
          "aside",
          {
            ref,
            "data-state": open ? "open" : "closed",
            className: cn(
              "bg-background border-border fixed inset-y-0 z-50 flex w-[var(--sidebar-width)] flex-col border-r transition-transform duration-300",
              side === "left" ? "left-0" : "right-0",
              !open && (side === "left" ? "-translate-x-full" : "translate-x-full"),
              className
            ),
            ...props,
            children
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsx(
      "aside",
      {
        ref,
        "data-state": open ? "expanded" : "collapsed",
        className: cn(
          "bg-background border-border flex h-full flex-col border-r transition-all duration-300",
          open ? "w-[var(--sidebar-width)]" : "w-[var(--sidebar-width-collapsed)]",
          className
        ),
        ...props,
        children
      }
    );
  }
);
Sidebar.displayName = "Sidebar";
var SidebarHeader = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("border-border border-b px-3 py-4", className),
    ...props
  }
));
SidebarHeader.displayName = "SidebarHeader";
var SidebarContent = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex-1 overflow-y-auto p-2", className),
    ...props
  }
));
SidebarContent.displayName = "SidebarContent";
var SidebarFooter = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("border-border border-t p-3", className),
    ...props
  }
));
SidebarFooter.displayName = "SidebarFooter";
var SidebarGroup = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("mb-4", className), ...props }));
SidebarGroup.displayName = "SidebarGroup";
var SidebarGroupLabel = React17.forwardRef(({ className, ...props }, ref) => {
  const { open } = useSidebar();
  if (!open) return null;
  return /* @__PURE__ */ jsx(
    "h3",
    {
      ref,
      className: cn(
        "text-foreground/60 px-3 py-2 text-sm font-semibold tracking-wider uppercase",
        className
      ),
      ...props
    }
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";
var SidebarGroupContent = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn(className), ...props }));
SidebarGroupContent.displayName = "SidebarGroupContent";
var SidebarMenu = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "ul",
  {
    ref,
    className: cn("space-y-0.5", className),
    role: "menu",
    ...props
  }
));
SidebarMenu.displayName = "SidebarMenu";
var SidebarMenuItem = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("li", { ref, className: cn(className), role: "none", ...props }));
SidebarMenuItem.displayName = "SidebarMenuItem";
var SidebarMenuButton = React17.forwardRef(({ className, isActive = false, tooltip, children, ...props }, ref) => {
  const { open } = useSidebar();
  return /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      role: "menuitem",
      "aria-current": isActive ? "page" : void 0,
      title: !open ? tooltip : void 0,
      "aria-label": !open ? tooltip : void 0,
      className: cn(
        "flex w-full items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors duration-150",
        "focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
        isActive ? "bg-primary/20 text-white" : "text-foreground/70 hover:bg-surface-1 hover:text-foreground",
        !open && "justify-center px-2",
        className
      ),
      ...props,
      children
    }
  );
});
SidebarMenuButton.displayName = "SidebarMenuButton";
var SidebarMenuBadge = React17.forwardRef(({ className, ...props }, ref) => {
  const { open } = useSidebar();
  if (!open) return null;
  return /* @__PURE__ */ jsx(
    "span",
    {
      ref,
      className: cn(
        "bg-primary text-primary-foreground ml-auto rounded-full px-2 py-0.5 text-sm font-medium",
        className
      ),
      ...props
    }
  );
});
SidebarMenuBadge.displayName = "SidebarMenuBadge";
var SidebarTrigger = React17.forwardRef(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar, open } = useSidebar();
  return /* @__PURE__ */ jsx(
    Button,
    {
      ref,
      variant: "ghost",
      size: "icon",
      className: cn("h-8 w-8", className),
      "aria-label": open ? "Collapse sidebar" : "Expand sidebar",
      onClick: (event) => {
        onClick?.(event);
        toggleSidebar();
      },
      ...props,
      children: /* @__PURE__ */ jsx(PanelLeft, { className: "h-4 w-4" })
    }
  );
});
SidebarTrigger.displayName = "SidebarTrigger";
var SidebarInset = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex-1 overflow-auto", className), ...props }));
SidebarInset.displayName = "SidebarInset";
var variantClasses = {
  text: "h-4 w-full rounded",
  circle: "rounded-full",
  rectangle: "rounded-md"
};
function Skeleton({
  className,
  variant = "rectangle",
  width,
  height,
  style,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "skeleton",
      className: cn(
        "bg-accent animate-pulse",
        variantClasses[variant],
        className
      ),
      style: {
        width: typeof width === "number" ? `${width}px` : width,
        height: typeof height === "number" ? `${height}px` : height,
        ...style
      },
      ...props
    }
  );
}
function Toaster(props) {
  return /* @__PURE__ */ jsx(
    Toaster$1,
    {
      theme: "dark",
      position: "bottom-right",
      toastOptions: {
        classNames: {
          toast: "group border-border bg-surface-2 text-foreground shadow-lg rounded-lg",
          title: "text-foreground font-medium",
          description: "text-foreground/60",
          actionButton: "bg-primary text-primary-foreground",
          cancelButton: "bg-surface-3 text-foreground/60",
          closeButton: "text-foreground/60 hover:text-foreground",
          success: "border-success/20 [&_[data-icon]]:text-success",
          error: "border-error/20 [&_[data-icon]]:text-error",
          warning: "border-warning/20 [&_[data-icon]]:text-warning",
          info: "border-primary/20 [&_[data-icon]]:text-primary"
        }
      },
      ...props
    }
  );
}
var surfaceVariants = cva("", {
  variants: {
    variant: {
      default: "",
      bordered: "border-border border-b",
      elevated: "border-border bg-surface-1 rounded-lg border shadow-sm",
      panel: "bg-surface-1"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var Surface = React17.forwardRef(
  ({ className, variant, as: Component = "div", ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      Component,
      {
        ref,
        "data-slot": "surface",
        className: cn(surfaceVariants({ variant }), className),
        ...props
      }
    );
  }
);
Surface.displayName = "Surface";
function Switch({ className, size = "default", ...props }) {
  return /* @__PURE__ */ jsx(
    SwitchPrimitive.Root,
    {
      "data-slot": "switch",
      "data-size": size,
      className: cn(
        "peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-input focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80 group/switch inline-flex shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-[1.15rem] data-[size=default]:w-8 data-[size=sm]:h-3.5 data-[size=sm]:w-6",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        SwitchPrimitive.Thumb,
        {
          "data-slot": "switch-thumb",
          className: cn(
            "bg-background dark:data-[state=unchecked]:bg-foreground dark:data-[state=checked]:bg-primary-foreground pointer-events-none block rounded-full ring-0 transition-transform group-data-[size=default]/switch:size-4 group-data-[size=sm]/switch:size-3 data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0"
          )
        }
      )
    }
  );
}
var tabsListVariants = cva("inline-flex items-center", {
  variants: {
    variant: {
      default: "border-b border-border",
      pills: "gap-1 bg-surface-3 p-1 rounded-lg",
      underline: "gap-4"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "px-4 py-2.5 border-b-2 -mb-px text-base text-foreground/60 hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-foreground border-transparent",
        pills: "px-3 py-1.5 rounded-md text-base text-foreground/60 hover:text-foreground data-[state=active]:bg-surface-1 data-[state=active]:text-foreground data-[state=active]:shadow-sm",
        underline: "px-1 py-2 text-base text-foreground/60 hover:text-foreground data-[state=active]:text-foreground relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:scale-x-0 data-[state=active]:after:scale-x-100 after:transition-transform after:duration-150"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Tabs = TabsPrimitive.Root;
var TabsList = React17.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.List,
  {
    ref,
    className: cn(tabsListVariants({ variant }), className),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React17.forwardRef(({ className, variant, icon: Icon3, badge, children, ...props }, ref) => /* @__PURE__ */ jsxs(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(tabsTriggerVariants({ variant }), className),
    ...props,
    children: [
      Icon3 && /* @__PURE__ */ jsx(Icon3, { className: "mr-2 h-4 w-4", "aria-hidden": "true" }),
      children,
      badge !== void 0 && badge > 0 && /* @__PURE__ */ jsx("span", { className: "bg-surface-3 text-foreground/60 ml-2 rounded-full px-1.5 py-0.5 text-sm font-medium", children: badge > 99 ? "99+" : badge })
    ]
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React17.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  TabsPrimitive.Content,
  {
    ref,
    className: cn("focus-visible:outline-none", className),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
var textareaClasses = "border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
function TextArea({
  className,
  error,
  errorMessage,
  autoResize = false,
  showCount = false,
  maxLength,
  onChange,
  id,
  ...props
}) {
  const [charCount, setCharCount] = React17.useState(0);
  const textAreaRef = React17.useRef(null);
  const errorId = React17.useId();
  const ariaDescribedBy = errorMessage ? errorId : props["aria-describedby"];
  const handleChange = (e) => {
    if (showCount) {
      setCharCount(e.target.value.length);
    }
    if (autoResize && textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
    onChange?.(e);
  };
  React17.useEffect(() => {
    if (showCount && props.value !== void 0) {
      setCharCount(String(props.value).length);
    } else if (showCount && props.defaultValue !== void 0) {
      setCharCount(String(props.defaultValue).length);
    }
  }, [showCount, props.value, props.defaultValue]);
  return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
    /* @__PURE__ */ jsx(
      "textarea",
      {
        "data-slot": "textarea",
        id,
        ref: textAreaRef,
        className: cn(textareaClasses, autoResize && "resize-none", className),
        "aria-invalid": error || void 0,
        "aria-describedby": ariaDescribedBy,
        maxLength,
        onChange: handleChange,
        ...props
      }
    ),
    (errorMessage || showCount) && /* @__PURE__ */ jsxs("div", { className: "mt-1.5 flex items-center justify-between", children: [
      errorMessage && /* @__PURE__ */ jsx("p", { id: errorId, className: "text-destructive text-sm", role: "alert", children: errorMessage }),
      showCount && /* @__PURE__ */ jsxs(
        "span",
        {
          className: cn(
            "text-muted-foreground ml-auto text-sm",
            maxLength && charCount > maxLength && "text-destructive"
          ),
          children: [
            charCount,
            maxLength && `/${maxLength}`
          ]
        }
      )
    ] })
  ] });
}
function TooltipProvider({
  delayDuration = 0,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    TooltipPrimitive.Provider,
    {
      "data-slot": "tooltip-provider",
      delayDuration,
      ...props
    }
  );
}
function Tooltip({
  ...props
}) {
  return /* @__PURE__ */ jsx(TooltipPrimitive.Root, { "data-slot": "tooltip", ...props });
}
function TooltipTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(TooltipPrimitive.Trigger, { "data-slot": "tooltip-trigger", ...props });
}
function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs(
    TooltipPrimitive.Content,
    {
      "data-slot": "tooltip-content",
      sideOffset,
      className: cn(
        "bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit rounded-md px-3 py-1.5 text-xs text-balance",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(TooltipPrimitive.Arrow, { className: "bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" })
      ]
    }
  ) });
}
function DividerWithText({
  className,
  text = "or",
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-slot": "divider-with-text",
      className: cn("flex items-center gap-4", className),
      role: "separator",
      "aria-label": text,
      ...props,
      children: [
        /* @__PURE__ */ jsx(Separator, { className: "flex-1" }),
        /* @__PURE__ */ jsx("span", { className: "text-muted-foreground text-sm", children: text }),
        /* @__PURE__ */ jsx(Separator, { className: "flex-1" })
      ]
    }
  );
}
var presets = {
  "no-data": {
    icon: Inbox,
    title: "No data yet",
    description: "Get started by creating your first item."
  },
  "no-results": {
    icon: Search,
    title: "No results found",
    description: "Try adjusting your search or filter criteria."
  },
  "no-projects": {
    icon: FolderOpen,
    title: "No projects",
    description: "Create a project to organize your requests."
  },
  "no-requests": {
    icon: FileQuestion,
    title: "No requests",
    description: "Create a request to get started."
  }
};
var sizeConfig = {
  sm: {
    container: "gap-3 p-4",
    iconContainer: "size-12",
    icon: "size-5",
    title: "text-base",
    desc: "text-sm",
    button: "default"
  },
  md: {
    container: "gap-4 p-8",
    iconContainer: "size-16",
    icon: "size-7",
    title: "text-base",
    desc: "text-base",
    button: "default"
  },
  lg: {
    container: "gap-6 p-12",
    iconContainer: "size-20",
    icon: "size-9",
    title: "text-lg",
    desc: "text-base",
    button: "lg"
  }
};
function EmptyState({
  className,
  size = "md",
  type = "custom",
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  illustration,
  ...props
}) {
  const preset = type !== "custom" ? presets[type] : null;
  const Icon3 = icon ?? preset?.icon ?? Inbox;
  const displayTitle = title ?? preset?.title ?? "Nothing here";
  const displayDescription = description ?? preset?.description;
  const s = sizeConfig[size];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "data-slot": "empty-state",
      className: cn(
        "flex flex-col items-center justify-center text-center",
        s.container,
        className
      ),
      ...props,
      children: [
        illustration ?? /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "bg-muted flex items-center justify-center rounded-full",
              s.iconContainer
            ),
            children: /* @__PURE__ */ jsx(
              Icon3,
              {
                className: cn(s.icon, "text-muted-foreground"),
                "aria-hidden": "true"
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
          /* @__PURE__ */ jsx("h3", { className: cn("text-foreground font-medium", s.title), children: displayTitle }),
          displayDescription && /* @__PURE__ */ jsx("p", { className: cn("text-muted-foreground max-w-md", s.desc), children: displayDescription })
        ] }),
        (actionLabel || secondaryActionLabel) && /* @__PURE__ */ jsxs("div", { className: "mt-2 flex flex-wrap gap-3", children: [
          actionLabel && onAction && /* @__PURE__ */ jsx(Button, { onClick: onAction, size: s.button, children: actionLabel }),
          secondaryActionLabel && onSecondaryAction && /* @__PURE__ */ jsx(
            Button,
            {
              variant: "outline",
              onClick: onSecondaryAction,
              size: s.button,
              children: secondaryActionLabel
            }
          )
        ] })
      ]
    }
  );
}
var GitHubIcon = React17.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "svg",
      {
        ref,
        viewBox: "0 0 24 24",
        fill: "currentColor",
        className: cn("inline-block", className),
        "aria-hidden": "true",
        ...props,
        children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" })
      }
    );
  }
);
GitHubIcon.displayName = "GitHubIcon";
var GoogleIcon = React17.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "svg",
      {
        ref,
        viewBox: "0 0 24 24",
        className: cn("inline-block", className),
        "aria-hidden": "true",
        ...props,
        children: [
          /* @__PURE__ */ jsx(
            "path",
            {
              d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z",
              fill: "#4285F4"
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z",
              fill: "#34A853"
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z",
              fill: "#FBBC05"
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z",
              fill: "#EA4335"
            }
          )
        ]
      }
    );
  }
);
GoogleIcon.displayName = "GoogleIcon";
var iconVariants = cva("shrink-0", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
      xl: "h-8 w-8"
    },
    color: {
      default: "text-foreground/60",
      primary: "text-primary",
      muted: "text-foreground/60",
      success: "text-success",
      warning: "text-warning",
      error: "text-error",
      info: "text-info"
    }
  },
  defaultVariants: {
    size: "md",
    color: "default"
  }
});
var Icon2 = React17.forwardRef(
  ({ className, icon: LucideIcon, size, color, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      LucideIcon,
      {
        ref,
        className: cn(iconVariants({ size, color, className })),
        ...props
      }
    );
  }
);
Icon2.displayName = "Icon";
var MicrosoftIcon = React17.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "svg",
      {
        ref,
        viewBox: "0 0 24 24",
        className: cn("inline-block", className),
        "aria-hidden": "true",
        ...props,
        children: [
          /* @__PURE__ */ jsx("rect", { x: "1", y: "1", width: "10", height: "10", fill: "#F25022" }),
          /* @__PURE__ */ jsx("rect", { x: "13", y: "1", width: "10", height: "10", fill: "#7FBA00" }),
          /* @__PURE__ */ jsx("rect", { x: "1", y: "13", width: "10", height: "10", fill: "#00A4EF" }),
          /* @__PURE__ */ jsx("rect", { x: "13", y: "13", width: "10", height: "10", fill: "#FFB900" })
        ]
      }
    );
  }
);
MicrosoftIcon.displayName = "MicrosoftIcon";
var iconButtonVariants = cva("rounded-md transition-colors duration-100", {
  variants: {
    size: {
      xs: "h-6 w-6",
      sm: "h-8 w-8",
      md: "h-9 w-9",
      lg: "h-10 w-10"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
var IconButton = React17.forwardRef(
  ({ size = "md", className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      Button,
      {
        ref,
        size: "icon",
        className: cn(iconButtonVariants({ size }), className),
        ...props,
        children
      }
    );
  }
);
IconButton.displayName = "IconButton";
var FormField = React17.forwardRef(
  ({
    label,
    id,
    errorMessage,
    required,
    containerClassName,
    error,
    className,
    ...inputProps
  }, ref) => {
    const hasError = error || !!errorMessage;
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", containerClassName), children: [
      /* @__PURE__ */ jsx(
        Label2,
        {
          htmlFor: id,
          variant: hasError ? "error" : "default",
          className: "text-foreground text-base font-medium",
          ...required && { required },
          children: label
        }
      ),
      /* @__PURE__ */ jsx(
        Input,
        {
          id,
          ref,
          error: hasError,
          "aria-describedby": errorMessage ? `${id}-error` : void 0,
          "aria-required": required,
          className,
          ...inputProps
        }
      ),
      errorMessage && /* @__PURE__ */ jsx("span", { id: `${id}-error`, className: "text-error text-sm", role: "alert", children: errorMessage })
    ] });
  }
);
FormField.displayName = "FormField";
var socialLoginButtonVariants = cva(
  "inline-flex items-center justify-center gap-3 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        branded: "bg-surface-2 text-foreground border border-border hover:bg-surface-3",
        outline: "bg-transparent text-foreground border border-border hover:bg-surface-2"
      },
      size: {
        md: "h-11 px-4 py-2",
        lg: "h-12 px-6 py-3 text-base"
      },
      fullWidth: {
        true: "w-full",
        false: ""
      }
    },
    defaultVariants: {
      variant: "branded",
      size: "md",
      fullWidth: false
    }
  }
);
var SocialLoginButton = React17.forwardRef(
  ({
    className,
    variant,
    size,
    fullWidth,
    provider,
    loading = false,
    disabled,
    children,
    ...props
  }, ref) => {
    const providerConfig = {
      google: {
        icon: GoogleIcon,
        label: "Continue with Google",
        loadingLabel: "Signing in..."
      },
      microsoft: {
        icon: MicrosoftIcon,
        label: "Continue with Microsoft",
        loadingLabel: "Signing in..."
      }
    };
    const config = providerConfig[provider];
    const Icon3 = config.icon;
    const buttonText = children || config.label;
    return /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        type: "button",
        className: cn(
          socialLoginButtonVariants({ variant, size, fullWidth, className })
        ),
        disabled: disabled || loading,
        "aria-busy": loading,
        "aria-label": loading ? config.loadingLabel : config.label,
        ...props,
        children: loading ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm", variant: "default", label: config.loadingLabel }),
          /* @__PURE__ */ jsx("span", { children: config.loadingLabel })
        ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Icon3, { className: "h-5 w-5" }),
          /* @__PURE__ */ jsx("span", { children: buttonText })
        ] })
      }
    );
  }
);
SocialLoginButton.displayName = "SocialLoginButton";
function LoginForm({
  onGoogleLogin,
  onMicrosoftLogin,
  onEmailLogin,
  onForgotPassword,
  googleLoading = false,
  microsoftLoading = false,
  emailLoading = false,
  errorMessage,
  emailError,
  passwordError,
  showSocialLogins = true,
  showEmailLogin = true,
  showForgotPassword = true,
  className
}) {
  const [email, setEmail] = React17.useState("");
  const [password, setPassword] = React17.useState("");
  const [internalEmailError, setInternalEmailError] = React17.useState("");
  const [internalPasswordError, setInternalPasswordError] = React17.useState("");
  const isAnyLoading = googleLoading || microsoftLoading || emailLoading;
  const handleSubmit = async (e) => {
    e.preventDefault();
    setInternalEmailError("");
    setInternalPasswordError("");
    if (!email) {
      setInternalEmailError("Email is required");
      return;
    }
    if (!password) {
      setInternalPasswordError("Password is required");
      return;
    }
    if (onEmailLogin) {
      await onEmailLogin(email, password);
    }
  };
  const displayEmailError = emailError || internalEmailError;
  const displayPasswordError = passwordError || internalPasswordError;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-6", className), children: [
    errorMessage && /* @__PURE__ */ jsx(
      "div",
      {
        className: "border-error bg-error/10 text-error rounded-md border px-4 py-3 text-sm",
        role: "alert",
        children: errorMessage
      }
    ),
    showSocialLogins && /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
      onGoogleLogin && /* @__PURE__ */ jsx(
        SocialLoginButton,
        {
          provider: "google",
          onClick: onGoogleLogin,
          loading: googleLoading,
          disabled: isAnyLoading && !googleLoading,
          fullWidth: true
        }
      ),
      onMicrosoftLogin && /* @__PURE__ */ jsx(
        SocialLoginButton,
        {
          provider: "microsoft",
          onClick: onMicrosoftLogin,
          loading: microsoftLoading,
          disabled: isAnyLoading && !microsoftLoading,
          fullWidth: true
        }
      )
    ] }),
    showSocialLogins && showEmailLogin && /* @__PURE__ */ jsx(DividerWithText, { text: "or" }),
    showEmailLogin && /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, className: "space-y-5", children: [
      /* @__PURE__ */ jsx(
        FormField,
        {
          id: "login-email",
          label: "Email",
          type: "email",
          placeholder: "you@example.com",
          value: email,
          onChange: (e) => setEmail(e.target.value),
          errorMessage: displayEmailError,
          required: true,
          disabled: isAnyLoading
        }
      ),
      /* @__PURE__ */ jsx(
        FormField,
        {
          id: "login-password",
          label: "Password",
          type: "password",
          placeholder: "Enter your password",
          value: password,
          onChange: (e) => setPassword(e.target.value),
          errorMessage: displayPasswordError,
          required: true,
          disabled: isAnyLoading
        }
      ),
      showForgotPassword && onForgotPassword && /* @__PURE__ */ jsx("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: onForgotPassword,
          className: "text-primary hover:text-primary-hover text-sm transition-colors duration-100 disabled:opacity-50",
          disabled: isAnyLoading,
          children: "Forgot password?"
        }
      ) }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "submit",
          variant: "default",
          size: "lg",
          disabled: isAnyLoading,
          loading: emailLoading,
          className: "w-full",
          children: "Sign in"
        }
      )
    ] })
  ] });
}
LoginForm.displayName = "LoginForm";
var orgLogoVariants = cva(
  "relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface-2 font-semibold text-foreground",
  {
    variants: {
      size: {
        xs: "h-6 w-6 text-2xs",
        sm: "h-8 w-8 text-sm",
        md: "h-10 w-10 text-base",
        lg: "h-12 w-12 text-base"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
function getInitials2(name) {
  if (!name) return "?";
  const words = name.trim().split(/\s+/);
  const firstWord = words[0] ?? "";
  const secondWord = words[1] ?? "";
  if (words.length === 1 || !secondWord) {
    return firstWord.slice(0, 2).toUpperCase();
  }
  const first = firstWord[0] ?? "";
  const second = secondWord[0] ?? "";
  return (first + second).toUpperCase();
}
var OrgLogo = React17.forwardRef(
  ({ className, size, src, name, ...props }, ref) => {
    const [imageError, setImageError] = React17.useState(false);
    const initials = getInitials2(name);
    const showImage = src && !imageError;
    React17.useEffect(() => {
      setImageError(false);
    }, [src]);
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(orgLogoVariants({ size, className })),
        role: "img",
        "aria-label": `${name} logo`,
        ...props,
        children: showImage ? /* @__PURE__ */ jsx(
          "img",
          {
            src,
            alt: `${name} logo`,
            className: "h-full w-full object-cover",
            onError: () => setImageError(true)
          }
        ) : initials
      }
    );
  }
);
OrgLogo.displayName = "OrgLogo";
function SearchField({
  className,
  value,
  onValueChange,
  onClear,
  loading = false,
  debounceMs = 0,
  placeholder = "Search...",
  disabled,
  regionLabel,
  onChange,
  ...props
}) {
  const [internalValue, setInternalValue] = React17.useState(
    value ?? ""
  );
  const debounceTimerRef = React17.useRef(
    null
  );
  React17.useEffect(() => {
    if (value !== void 0) {
      setInternalValue(value);
    }
  }, [value]);
  React17.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  const handleChange = (e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (debounceMs > 0) {
      debounceTimerRef.current = setTimeout(() => {
        onValueChange?.(newValue);
      }, debounceMs);
    } else {
      onValueChange?.(newValue);
    }
    onChange?.(e);
  };
  const handleClear = () => {
    setInternalValue("");
    onValueChange?.("");
    onClear?.();
  };
  const showClearButton = internalValue.length > 0 && !disabled;
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-slot": "search-field",
      role: "search",
      "aria-label": regionLabel,
      className: cn("relative", className),
      children: /* @__PURE__ */ jsx(
        Input,
        {
          type: "search",
          value: internalValue,
          onChange: handleChange,
          placeholder,
          disabled,
          "aria-label": props["aria-label"] ?? "Search",
          leftAddon: loading ? /* @__PURE__ */ jsx(Loader2, { className: "size-4 animate-spin", "aria-hidden": "true" }) : /* @__PURE__ */ jsx(Search, { className: "size-4", "aria-hidden": "true" }),
          rightAddon: showClearButton ? /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: handleClear,
              "aria-label": "Clear search",
              className: "text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-sm transition-colors focus-visible:ring-[2px] focus-visible:outline-none",
              children: /* @__PURE__ */ jsx(X, { className: "size-4", "aria-hidden": "true" })
            }
          ) : void 0,
          className: "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none",
          ...props
        }
      )
    }
  );
}
var statCardVariants = cva(
  "flex flex-col gap-2 rounded-lg border p-4 transition-colors duration-150",
  {
    variants: {
      variant: {
        default: "bg-surface-1 border-border",
        primary: "bg-primary/5 border-primary/20",
        success: "bg-success/5 border-success/20",
        warning: "bg-warning/5 border-warning/20"
      },
      interactive: {
        true: "cursor-pointer hover:bg-surface-2",
        false: ""
      }
    },
    defaultVariants: {
      variant: "default",
      interactive: false
    }
  }
);
var iconContainerVariants = cva("flex-center h-8 w-8 shrink-0 rounded-md", {
  variants: {
    variant: {
      default: "bg-surface-3 text-foreground/70",
      primary: "bg-primary/10 text-primary",
      success: "bg-success/10 text-success",
      warning: "bg-warning/10 text-warning"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var trendIcons = {
  up: ArrowUp,
  down: ArrowDown,
  neutral: Minus
};
var trendColors = {
  up: "text-success",
  down: "text-error",
  neutral: "text-foreground/60"
};
var StatCard = React17.forwardRef(
  ({ className, variant, label, value, icon, trend, onClick, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        role: onClick ? "button" : void 0,
        tabIndex: onClick ? 0 : void 0,
        onClick,
        onKeyDown: onClick ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        } : void 0,
        className: cn(
          statCardVariants({ variant, interactive: !!onClick }),
          onClick && "focus-ring",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsx("span", { className: "text-foreground/60 text-sm", children: label }),
            icon && /* @__PURE__ */ jsx("div", { className: cn(iconContainerVariants({ variant })), children: React17.isValidElement(icon) ? React17.cloneElement(
              icon,
              {
                className: "h-4 w-4"
              }
            ) : icon })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "text-foreground text-2xl font-semibold", children: value }),
            trend && /* @__PURE__ */ jsxs(
              "span",
              {
                className: cn(
                  "mb-0.5 flex items-center gap-0.5 text-xs font-medium",
                  trendColors[trend.direction]
                ),
                children: [
                  React17.createElement(trendIcons[trend.direction], {
                    className: "h-3 w-3"
                  }),
                  trend.value !== 0 && Math.abs(trend.value)
                ]
              }
            )
          ] })
        ]
      }
    );
  }
);
StatCard.displayName = "StatCard";
var UserMenu = React17.forwardRef(
  ({
    user,
    collapsed = false,
    onAdminClick,
    onSettingsClick,
    onLogoutClick,
    className,
    align = "end",
    side = "top"
  }, ref) => {
    const isAdmin = user.role === "admin";
    const initials = getInitials3(user.name || user.email);
    return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
      /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
        "button",
        {
          ref,
          className: cn(
            "flex w-full items-center gap-3 rounded-md p-1 text-left transition-colors duration-100",
            "hover:bg-accent",
            "focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
            collapsed && "justify-center",
            className
          ),
          "aria-label": "Open user menu",
          children: [
            /* @__PURE__ */ jsx(
              Avatar,
              {
                src: user.avatarUrl ?? "",
                fallback: initials,
                size: "sm",
                className: "shrink-0"
              }
            ),
            !collapsed && /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
              /* @__PURE__ */ jsx("div", { className: "text-foreground truncate text-sm font-medium", children: user.name || user.email }),
              user.organization && /* @__PURE__ */ jsx("span", { className: "text-foreground/60 truncate text-sm", children: user.organization.name })
            ] })
          ]
        }
      ) }),
      /* @__PURE__ */ jsxs(DropdownMenuContent, { align, side, className: "w-56", children: [
        /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col space-y-1", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm leading-none font-medium", children: user.name || "User" }),
          /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-sm leading-none", children: user.email })
        ] }) }),
        /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
        isAdmin && onAdminClick && /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: onAdminClick, children: [
          /* @__PURE__ */ jsx(Shield, { className: "h-4 w-4" }),
          "Admin Dashboard"
        ] }),
        onSettingsClick && /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: onSettingsClick, children: [
          /* @__PURE__ */ jsx(Settings, { className: "h-4 w-4" }),
          "Settings"
        ] }),
        (isAdmin || onSettingsClick) && onLogoutClick && /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
        onLogoutClick && /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: onLogoutClick, variant: "destructive", children: [
          /* @__PURE__ */ jsx(LogOut, { className: "h-4 w-4" }),
          "Log out"
        ] })
      ] })
    ] });
  }
);
UserMenu.displayName = "UserMenu";
function getInitials3(nameOrEmail) {
  if (!nameOrEmail) return "?";
  if (nameOrEmail.includes("@")) {
    return nameOrEmail.charAt(0).toUpperCase();
  }
  const parts = nameOrEmail.trim().split(/\s+/);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first) return "?";
  if (parts.length === 1 || !last) {
    return first.charAt(0).toUpperCase();
  }
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}
var activityItemVariants = cva(
  "flex items-start gap-3 p-3 rounded-lg transition-colors duration-150",
  {
    variants: {
      variant: {
        default: "hover:bg-surface-2",
        compact: "p-2"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var iconContainerVariants2 = cva(
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
  {
    variants: {
      type: {
        info: "bg-info/10 text-info",
        success: "bg-success/10 text-success",
        warning: "bg-warning/10 text-warning",
        error: "bg-error/10 text-error",
        code: "bg-primary/10 text-primary",
        message: "bg-info/10 text-info",
        deploy: "bg-success/10 text-success",
        config: "bg-muted-foreground/10 text-foreground/60",
        document: "bg-primary/10 text-primary",
        automated: "bg-warning/10 text-warning"
      }
    },
    defaultVariants: {
      type: "info"
    }
  }
);
var activityIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertCircle,
  error: AlertCircle,
  code: Code,
  message: MessageSquare,
  deploy: Play,
  config: Settings,
  document: FileText,
  automated: Zap
};
var formatRelativeTime = (date) => {
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 6e4);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};
var formatAbsoluteTime = (date) => {
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};
var ActivityItem = React17.forwardRef(
  ({
    className,
    variant,
    type,
    message,
    timestamp,
    details,
    icon,
    relativeTime = true,
    ...props
  }, ref) => {
    const [expanded, setExpanded] = React17.useState(false);
    const Icon3 = icon ?? activityIcons[type];
    const formattedTime = relativeTime ? formatRelativeTime(timestamp) : formatAbsoluteTime(timestamp);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(activityItemVariants({ variant }), className),
        ...props,
        children: [
          /* @__PURE__ */ jsx("div", { className: iconContainerVariants2({ type }), children: /* @__PURE__ */ jsx(Icon3, { className: "h-4 w-4", "aria-hidden": "true" }) }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx("p", { className: "text-foreground text-base", children: message }),
            details && /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => setExpanded(!expanded),
                className: "text-primary mt-1 text-sm hover:underline focus:outline-none",
                children: expanded ? "Hide details" : "Show details"
              }
            ),
            expanded && details && /* @__PURE__ */ jsx("div", { className: "bg-surface-2 text-foreground/60 mt-2 rounded-md p-2 font-mono text-sm", children: details })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "text-foreground/60 flex shrink-0 items-center gap-1 text-sm", children: [
            /* @__PURE__ */ jsx(Clock, { className: "h-3 w-3", "aria-hidden": "true" }),
            /* @__PURE__ */ jsx(
              "time",
              {
                dateTime: timestamp.toISOString(),
                title: formatAbsoluteTime(timestamp),
                children: formattedTime
              }
            )
          ] })
        ]
      }
    );
  }
);
ActivityItem.displayName = "ActivityItem";
var toolCallCardVariants = cva(
  "rounded-lg border text-sm font-mono overflow-hidden",
  {
    variants: {
      status: {
        executing: "border-primary/30 bg-primary/5",
        success: "border-success/30 bg-success/5",
        failure: "border-error/30 bg-error/5",
        pending: "border-border bg-surface-2"
      }
    },
    defaultVariants: {
      status: "pending"
    }
  }
);
var TOOL_ICONS = {
  execute_command: Terminal,
  read_file: FileText,
  write_file: Pencil,
  list_files: FolderOpen
};
var TOOL_LABELS = {
  execute_command: "Running command",
  read_file: "Reading file",
  write_file: "Writing file",
  list_files: "Listing files"
};
function getToolSummary(toolName, toolInput) {
  switch (toolName) {
    case "execute_command":
      return String(toolInput.command ?? "");
    case "read_file":
    case "write_file":
      return String(toolInput.path ?? "");
    case "list_files":
      return String(toolInput.path ?? ".");
    default:
      return toolName;
  }
}
var ToolCallCard = React17.forwardRef(
  ({
    toolName,
    toolInput,
    output,
    success,
    durationMs,
    isExecuting = false,
    className
  }, ref) => {
    const [isExpanded, setIsExpanded] = React17.useState(false);
    const Icon3 = TOOL_ICONS[toolName] ?? Terminal;
    const label = TOOL_LABELS[toolName] ?? toolName;
    const summary = getToolSummary(toolName, toolInput);
    const status = isExecuting ? "executing" : success === true ? "success" : success === false ? "failure" : "pending";
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(toolCallCardVariants({ status }), className),
        children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: "flex w-full items-center gap-2 px-3 py-2 text-left",
              onClick: () => setIsExpanded((prev) => !prev),
              children: [
                isExecuting ? /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-4 w-4 shrink-0 animate-spin" }) : /* @__PURE__ */ jsx(Icon3, { className: "text-foreground/60 h-4 w-4 shrink-0" }),
                /* @__PURE__ */ jsxs("span", { className: "text-foreground/80 truncate text-xs", children: [
                  label,
                  ":",
                  " ",
                  /* @__PURE__ */ jsx("span", { className: "text-foreground font-medium", children: summary })
                ] }),
                durationMs !== void 0 && /* @__PURE__ */ jsx("span", { className: "text-foreground/40 ml-auto shrink-0 text-xs", children: durationMs < 1e3 ? `${durationMs}ms` : `${(durationMs / 1e3).toFixed(1)}s` }),
                isExpanded ? /* @__PURE__ */ jsx(ChevronDown, { className: "text-foreground/40 h-3 w-3 shrink-0" }) : /* @__PURE__ */ jsx(ChevronRight, { className: "text-foreground/40 h-3 w-3 shrink-0" })
              ]
            }
          ),
          isExpanded && output && /* @__PURE__ */ jsx("div", { className: "border-t border-inherit px-3 py-2", children: /* @__PURE__ */ jsx("pre", { className: "text-foreground/70 max-h-48 overflow-auto text-xs whitespace-pre-wrap", children: output }) })
        ]
      }
    );
  }
);
ToolCallCard.displayName = "ToolCallCard";
var AgentMessage = React17.forwardRef(
  ({ role, content, toolCalls, isStreaming = false, className }, ref) => {
    const isUser = role === "user";
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "flex gap-3",
          isUser ? "flex-row-reverse" : "flex-row",
          className
        ),
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                isUser ? "bg-primary text-primary-foreground" : "bg-surface-3 text-foreground/70"
              ),
              children: isUser ? /* @__PURE__ */ jsx(User, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(Bot, { className: "h-4 w-4" })
            }
          ),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                "flex max-w-[85%] min-w-0 flex-col gap-2",
                isUser ? "items-end" : "items-start"
              ),
              children: [
                (content || isStreaming) && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "rounded-2xl px-4 py-2.5 text-sm",
                      isUser ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-surface-2 text-foreground rounded-bl-sm"
                    ),
                    children: /* @__PURE__ */ jsxs("div", { className: "break-words whitespace-pre-wrap", children: [
                      content,
                      isStreaming && /* @__PURE__ */ jsx("span", { className: "bg-foreground ml-0.5 inline-block h-4 w-0.5 animate-pulse" })
                    ] })
                  }
                ),
                toolCalls && toolCalls.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex w-full flex-col gap-1.5", children: toolCalls.map((tc) => /* @__PURE__ */ jsx(
                  ToolCallCard,
                  {
                    toolName: tc.name,
                    toolInput: tc.input,
                    output: tc.output,
                    success: tc.success,
                    durationMs: tc.durationMs,
                    isExecuting: tc.isExecuting
                  },
                  tc.id
                )) })
              ]
            }
          )
        ]
      }
    );
  }
);
AgentMessage.displayName = "AgentMessage";
var chatBubbleVariants = cva(
  "max-w-[var(--chat-bubble-max-width)] rounded-2xl px-4 py-2.5 text-base",
  {
    variants: {
      variant: {
        user: "bg-primary text-primary-foreground ml-auto rounded-br-sm",
        assistant: "bg-surface-2 text-foreground rounded-bl-sm"
      }
    },
    defaultVariants: {
      variant: "user"
    }
  }
);
var containerVariants2 = cva("flex gap-3", {
  variants: {
    variant: {
      user: "flex-row-reverse",
      assistant: "flex-row"
    }
  },
  defaultVariants: {
    variant: "user"
  }
});
var TypingIndicator = () => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 px-1 py-1", children: [
  /* @__PURE__ */ jsx(
    "span",
    {
      className: "bg-muted-foreground h-2 w-2 animate-bounce rounded-full",
      style: { animationDelay: "0ms" }
    }
  ),
  /* @__PURE__ */ jsx(
    "span",
    {
      className: "bg-muted-foreground h-2 w-2 animate-bounce rounded-full",
      style: { animationDelay: "150ms" }
    }
  ),
  /* @__PURE__ */ jsx(
    "span",
    {
      className: "bg-muted-foreground h-2 w-2 animate-bounce rounded-full",
      style: { animationDelay: "300ms" }
    }
  )
] });
var formatTime = (date) => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
var ChatBubble = React17.forwardRef(
  ({
    className,
    variant = "user",
    content,
    timestamp,
    showTimestamp = true,
    status,
    avatarSrc,
    avatarFallback,
    showAvatar = true,
    isTyping = false,
    ...props
  }, ref) => {
    const isUser = variant === "user";
    const StatusIcon = () => {
      if (!isUser || !status) return null;
      switch (status) {
        case "sending":
          return /* @__PURE__ */ jsx(
            Loader2,
            {
              className: "text-foreground/60 h-3 w-3 animate-spin",
              "aria-label": "Sending message"
            }
          );
        case "sent":
          return /* @__PURE__ */ jsx(Check, { className: "text-success h-3 w-3", "aria-label": "Message sent" });
        case "error":
          return /* @__PURE__ */ jsx(
            AlertCircle,
            {
              className: "text-error h-3 w-3",
              "aria-label": "Failed to send"
            }
          );
        default:
          return null;
      }
    };
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(containerVariants2({ variant }), className),
        ...props,
        children: [
          showAvatar && /* @__PURE__ */ jsx("div", { className: "shrink-0 self-end", children: /* @__PURE__ */ jsx(
            Avatar,
            {
              size: "sm",
              ...avatarSrc && { src: avatarSrc },
              fallback: avatarFallback ?? (isUser ? "U" : "AI")
            }
          ) }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
            /* @__PURE__ */ jsx("div", { className: chatBubbleVariants({ variant }), children: isTyping ? /* @__PURE__ */ jsx(TypingIndicator, {}) : content }),
            showTimestamp && timestamp && !isTyping && /* @__PURE__ */ jsxs(
              "div",
              {
                className: cn(
                  "text-foreground/60 flex items-center gap-1.5 text-sm",
                  isUser ? "justify-end" : "justify-start"
                ),
                children: [
                  /* @__PURE__ */ jsx("span", { children: formatTime(timestamp) }),
                  /* @__PURE__ */ jsx(StatusIcon, {})
                ]
              }
            )
          ] })
        ]
      }
    );
  }
);
ChatBubble.displayName = "ChatBubble";
async function resizeImage(file, maxDimension) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = height / width * maxDimension;
          width = maxDimension;
        } else {
          width = width / height * maxDimension;
          height = maxDimension;
        }
      }
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Could not create blob"));
            return;
          }
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
          resolve(resizedFile);
        },
        file.type,
        0.9
      );
    };
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = URL.createObjectURL(file);
  });
}
var sizeClasses = {
  sm: "h-16 w-16",
  md: "h-24 w-24",
  lg: "h-32 w-32"
};
var ImageUpload = React17.forwardRef(
  ({
    src,
    placeholder,
    alt = "Uploaded image",
    onUpload,
    onDelete,
    maxDimension = 256,
    accept = "image/png,image/jpeg,image/webp",
    uploading = false,
    deleting = false,
    error,
    className,
    disabled = false,
    size = "md"
  }, ref) => {
    const inputRef = React17.useRef(null);
    const [isDragging, setIsDragging] = React17.useState(false);
    const [localError, setLocalError] = React17.useState(null);
    const displayError = error ?? localError;
    const isLoading = uploading || deleting;
    const isDisabled = disabled || isLoading;
    const handleFileSelect = async (file) => {
      setLocalError(null);
      const allowedTypes = accept.split(",").map((t) => t.trim());
      if (!allowedTypes.includes(file.type)) {
        setLocalError("Invalid file type");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setLocalError("File too large (max 5MB)");
        return;
      }
      try {
        const resizedFile = await resizeImage(file, maxDimension);
        await onUpload(resizedFile);
      } catch {
        setLocalError("Failed to process image");
      }
    };
    const handleInputChange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      e.target.value = "";
    };
    const handleDragOver = (e) => {
      e.preventDefault();
      if (!isDisabled) {
        setIsDragging(true);
      }
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      setIsDragging(false);
    };
    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      if (isDisabled) return;
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    };
    const handleClick = () => {
      if (!isDisabled) {
        inputRef.current?.click();
      }
    };
    const handleDelete = async (e) => {
      e.stopPropagation();
      if (onDelete && !isDisabled) {
        setLocalError(null);
        try {
          await onDelete();
        } catch {
          setLocalError("Failed to delete");
        }
      }
    };
    const handleClearError = (e) => {
      e.stopPropagation();
      setLocalError(null);
    };
    return /* @__PURE__ */ jsxs("div", { ref, className: cn("relative inline-block", className), children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          ref: inputRef,
          type: "file",
          accept,
          onChange: handleInputChange,
          className: "hidden",
          disabled: isDisabled
        }
      ),
      /* @__PURE__ */ jsxs(
        "div",
        {
          role: "button",
          tabIndex: isDisabled ? -1 : 0,
          onClick: handleClick,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick();
            }
          },
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop,
          className: cn(
            "group relative flex cursor-pointer items-center justify-center overflow-hidden rounded-md transition-all duration-150",
            "border-border border-2 border-dashed",
            "focus-visible:ring-primary focus:outline-none focus-visible:ring-2",
            sizeClasses[size],
            isDragging && "border-primary bg-primary/10",
            isDisabled && "cursor-not-allowed opacity-50",
            displayError && "border-error"
          ),
          "aria-label": "Upload image",
          children: [
            src ? /* @__PURE__ */ jsx("img", { src, alt, className: "h-full w-full object-cover" }) : placeholder,
            !isLoading && !isDisabled && /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity duration-100",
                  "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
                ),
                children: /* @__PURE__ */ jsx(Camera, { className: "h-6 w-6 text-white" })
              }
            ),
            isLoading && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-black/50", children: /* @__PURE__ */ jsx(Loader2, { className: "h-6 w-6 animate-spin text-white" }) })
          ]
        }
      ),
      src && onDelete && !isLoading && !isDisabled && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: handleDelete,
          className: cn(
            "absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full",
            "bg-error text-destructive-foreground shadow-md",
            "transition-transform duration-100 hover:scale-110",
            "focus-visible:ring-error focus:outline-none focus-visible:ring-2"
          ),
          "aria-label": "Delete image",
          children: /* @__PURE__ */ jsx(Trash2, { className: "h-3 w-3" })
        }
      ),
      displayError && /* @__PURE__ */ jsxs("div", { className: "absolute right-0 -bottom-6 left-0 flex items-center justify-center gap-1", children: [
        /* @__PURE__ */ jsx("span", { className: "text-error text-sm", children: displayError }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: handleClearError,
            className: "text-error hover:text-error/80",
            "aria-label": "Clear error",
            children: /* @__PURE__ */ jsx(X, { className: "h-3 w-3" })
          }
        )
      ] })
    ] });
  }
);
ImageUpload.displayName = "ImageUpload";
var MetadataField = React17.forwardRef(
  ({
    className,
    label,
    children,
    valueClassName,
    valueSize,
    valueColor,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      Stack,
      {
        ref,
        gap: "xs",
        "data-slot": "metadata-field",
        className: cn(className),
        ...props,
        children: [
          /* @__PURE__ */ jsx(
            Text,
            {
              color: "muted",
              size: "xs",
              weight: "medium",
              className: "tracking-wide uppercase",
              children: label
            }
          ),
          /* @__PURE__ */ jsx(Text, { size: valueSize, color: valueColor, className: valueClassName, children })
        ]
      }
    );
  }
);
MetadataField.displayName = "MetadataField";
var navItemVariants = cva(
  "group flex items-center gap-3 rounded-md px-3 py-2 text-base transition-colors duration-100 focus-ring",
  {
    variants: {
      variant: {
        default: "text-foreground/60 hover:bg-accent hover:text-foreground",
        active: "bg-primary/10 text-foreground font-medium"
      },
      size: {
        sm: "px-2 py-1.5 text-sm",
        md: "px-3 py-2 text-base",
        lg: "px-4 py-2.5 text-base"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md"
    }
  }
);
var iconSizes = {
  sm: "icon-sm",
  md: "icon-md",
  lg: "icon-lg"
};
var NavItem = React17.forwardRef(
  ({
    className,
    variant,
    size = "md",
    href,
    active = false,
    icon: Icon3,
    badge,
    collapsed = false,
    children,
    ...props
  }, ref) => {
    const iconClassName = iconSizes[size ?? "md"];
    const computedVariant = active ? "active" : variant;
    const content = /* @__PURE__ */ jsxs(Fragment, { children: [
      Icon3 && /* @__PURE__ */ jsx(
        Icon3,
        {
          className: cn(
            iconClassName,
            active ? "text-foreground" : "text-foreground/60 group-hover:text-foreground"
          ),
          "aria-hidden": "true"
        }
      ),
      !collapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children }),
        badge !== void 0 && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "ml-auto text-sm", children: badge })
      ] })
    ] });
    const accessibleName = collapsed && typeof children === "string" ? children : void 0;
    const baseProps = {
      className: cn(
        navItemVariants({ variant: computedVariant, size }),
        collapsed && "justify-center px-2",
        className
      ),
      "aria-current": active ? "page" : void 0,
      "aria-label": accessibleName
    };
    if (href) {
      return /* @__PURE__ */ jsx("a", { ref, href, ...baseProps, children: content });
    }
    return /* @__PURE__ */ jsx("button", { ref, type: "button", ...baseProps, ...props, children: content });
  }
);
NavItem.displayName = "NavItem";
var PipelineBar = React17.forwardRef(
  ({ className, segments, total, onSegmentClick, ...props }, ref) => {
    const activeSegments = segments.filter((s) => s.count > 0);
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("flex flex-col gap-3", className),
        ...props,
        children: [
          /* @__PURE__ */ jsx("div", { className: "bg-surface-3 flex h-3 overflow-hidden rounded-full", children: /* @__PURE__ */ jsx(TooltipProvider, { delayDuration: 150, children: activeSegments.map((segment) => {
            const widthPercent = total > 0 ? segment.count / total * 100 : 0;
            return /* @__PURE__ */ jsxs(Tooltip, { children: [
              /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: () => onSegmentClick?.(segment.stage),
                  disabled: !onSegmentClick,
                  className: cn(
                    "h-full transition-all duration-250",
                    onSegmentClick ? "cursor-pointer hover:opacity-80" : "cursor-default",
                    "focus-visible:ring-primary focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
                  ),
                  style: {
                    width: `${widthPercent}%`,
                    backgroundColor: segment.color,
                    minWidth: widthPercent > 0 ? "8px" : "0"
                  },
                  "aria-label": `${segment.label}: ${segment.count}`
                }
              ) }),
              /* @__PURE__ */ jsxs(TooltipContent, { children: [
                segment.label,
                ": ",
                segment.count
              ] })
            ] }, segment.stage);
          }) }) }),
          /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-x-5 gap-y-1", children: segments.map((segment) => /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => onSegmentClick?.(segment.stage),
              disabled: !onSegmentClick,
              className: cn(
                "flex items-center gap-1.5 text-xs",
                onSegmentClick ? "hover:text-foreground cursor-pointer" : "cursor-default",
                segment.count > 0 ? "text-foreground/70" : "text-foreground/40"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: "h-2.5 w-2.5 shrink-0 rounded-full",
                    style: { backgroundColor: segment.color }
                  }
                ),
                /* @__PURE__ */ jsx("span", { children: segment.label }),
                /* @__PURE__ */ jsx("span", { className: "text-foreground/50 font-medium", children: segment.count })
              ]
            },
            segment.stage
          )) })
        ]
      }
    );
  }
);
PipelineBar.displayName = "PipelineBar";
var stepCircleVariants = cva(
  "flex items-center justify-center rounded-full font-semibold transition-all duration-150",
  {
    variants: {
      state: {
        completed: "border-2 border-primary bg-primary text-primary-foreground",
        current: "border-2 border-primary bg-transparent text-primary",
        upcoming: "border border-border bg-surface-3 text-foreground/60"
      },
      size: {
        sm: "h-6 w-6 text-sm",
        md: "h-8 w-8 text-base",
        lg: "h-10 w-10 text-base"
      }
    },
    defaultVariants: {
      state: "upcoming",
      size: "md"
    }
  }
);
var stepLabelVariants = cva("transition-colors duration-150", {
  variants: {
    state: {
      completed: "text-foreground",
      current: "text-foreground font-semibold",
      upcoming: "text-foreground/60"
    },
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-base"
    }
  },
  defaultVariants: {
    state: "upcoming",
    size: "md"
  }
});
var stepDescriptionVariants = cva("transition-colors duration-150", {
  variants: {
    state: {
      completed: "text-foreground/60",
      current: "text-foreground/60",
      upcoming: "text-foreground/60"
    },
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-base"
    }
  },
  defaultVariants: {
    state: "upcoming",
    size: "md"
  }
});
var connectorVariants = cva("transition-colors duration-150", {
  variants: {
    orientation: {
      horizontal: "h-0.5 flex-1",
      vertical: "w-0.5 flex-1"
    },
    completed: {
      true: "bg-primary",
      false: "bg-border"
    }
  },
  defaultVariants: {
    orientation: "horizontal",
    completed: false
  }
});
var StepIndicator = React17.forwardRef(
  ({
    className,
    steps,
    currentStep = 0,
    orientation = "horizontal",
    size = "md",
    showLabels = true,
    showDescriptions = false,
    onStepClick,
    ...props
  }, ref) => {
    const getStepState = (index) => {
      if (index < currentStep) return "completed";
      if (index === currentStep) return "current";
      return "upcoming";
    };
    const containerClasses = cn(
      "flex",
      orientation === "horizontal" ? "flex-row items-start" : "flex-col",
      className
    );
    const stepItemClasses = cn(
      "flex",
      orientation === "horizontal" ? "flex-col items-center" : "flex-row items-start"
    );
    const connectorSpacing = {
      sm: orientation === "horizontal" ? "mx-2" : "my-2",
      md: orientation === "horizontal" ? "mx-3" : "my-3",
      lg: orientation === "horizontal" ? "mx-4" : "my-4"
    }[size ?? "md"];
    return /* @__PURE__ */ jsx(
      "ol",
      {
        ref,
        role: "list",
        "aria-label": `Progress: Step ${currentStep + 1} of ${steps.length}`,
        className: containerClasses,
        ...props,
        children: steps.map((step, index) => {
          const state = getStepState(index);
          const isClickable = onStepClick !== void 0;
          const isCompleted = state === "completed";
          const isCurrent = state === "current";
          const isLast = index === steps.length - 1;
          const ariaLabel = `Step ${index + 1}: ${step.label}${isCompleted ? ", completed" : ""}`;
          return /* @__PURE__ */ jsxs(React17.Fragment, { children: [
            /* @__PURE__ */ jsx("li", { className: cn(stepItemClasses, "relative"), children: /* @__PURE__ */ jsxs(
              "div",
              {
                className: cn(
                  "flex",
                  orientation === "horizontal" ? "flex-col items-center" : "flex-row items-start",
                  orientation === "vertical" && "w-full"
                ),
                children: [
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      disabled: !isClickable,
                      onClick: () => isClickable && onStepClick(index),
                      "aria-current": isCurrent ? "step" : void 0,
                      "aria-label": ariaLabel,
                      className: cn(
                        stepCircleVariants({ state, size }),
                        isClickable && "cursor-pointer hover:scale-105 active:scale-95",
                        !isClickable && "cursor-default",
                        "focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                        orientation === "vertical" && "shrink-0"
                      ),
                      children: isCompleted ? /* @__PURE__ */ jsx(
                        Check,
                        {
                          className: cn(
                            size === "sm" && "h-3 w-3",
                            size === "md" && "h-4 w-4",
                            size === "lg" && "h-5 w-5"
                          ),
                          "aria-hidden": "true"
                        }
                      ) : /* @__PURE__ */ jsx("span", { children: index + 1 })
                    }
                  ),
                  showLabels && /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: cn(
                        orientation === "horizontal" ? "mt-2 text-center" : "ml-3 flex-1"
                      ),
                      children: [
                        /* @__PURE__ */ jsx("div", { className: stepLabelVariants({ state, size }), children: step.label }),
                        showDescriptions && step.description && /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: cn(
                              stepDescriptionVariants({ state, size }),
                              orientation === "horizontal" ? "mt-1" : "mt-0.5"
                            ),
                            children: step.description
                          }
                        )
                      ]
                    }
                  )
                ]
              }
            ) }),
            !isLast && /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "flex items-center justify-center",
                  connectorSpacing,
                  orientation === "vertical" && "ml-3"
                ),
                "aria-hidden": "true",
                children: /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: connectorVariants({
                      orientation,
                      completed: isCompleted
                    })
                  }
                )
              }
            )
          ] }, step.id);
        })
      }
    );
  }
);
StepIndicator.displayName = "StepIndicator";
var activityFeedVariants = cva(
  "flex flex-col bg-surface-1 border border-border rounded-lg overflow-hidden",
  {
    variants: {
      size: {
        sm: "max-h-[var(--activity-feed-sm)]",
        md: "max-h-[var(--activity-feed-md)]",
        lg: "max-h-[var(--activity-feed-lg)]",
        full: "h-full"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var ActivityFeed = React17.forwardRef(
  ({
    className,
    size = "md",
    activities,
    title = "Activity",
    loading = false,
    onRefresh,
    onFilterClick,
    onActivityClick,
    onLoadMore,
    hasMore = false,
    emptyMessage = "No activity yet",
    showTimeline = true,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(activityFeedVariants({ size }), className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-between border-b p-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(Activity, { className: "text-foreground h-5 w-5" }),
              /* @__PURE__ */ jsx("h2", { className: "text-foreground font-medium", children: title })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
              onFilterClick && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onFilterClick,
                  "aria-label": "Filter activities",
                  children: /* @__PURE__ */ jsx(Filter, { className: "h-4 w-4" })
                }
              ),
              onRefresh && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onRefresh,
                  "aria-label": "Refresh activities",
                  disabled: loading,
                  className: cn(loading && "animate-spin"),
                  children: /* @__PURE__ */ jsx(RefreshCw, { className: "h-4 w-4" })
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto", tabIndex: 0, children: loading && activities.length === 0 ? /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-12", children: /* @__PURE__ */ jsx(Spinner, { size: "lg" }) }) : activities.length === 0 ? /* @__PURE__ */ jsxs("div", { className: "text-foreground/60 flex flex-col items-center justify-center py-12", children: [
            /* @__PURE__ */ jsx(Activity, { className: "mb-3 h-12 w-12 opacity-50" }),
            /* @__PURE__ */ jsx("p", { className: "text-base", children: emptyMessage })
          ] }) : /* @__PURE__ */ jsxs("div", { className: cn("relative", showTimeline && "pl-8"), children: [
            showTimeline && /* @__PURE__ */ jsx("div", { className: "bg-border absolute top-4 bottom-4 left-6 w-px" }),
            /* @__PURE__ */ jsx("ul", { className: "divide-border divide-y", children: activities.map((activity, index) => /* @__PURE__ */ jsxs("li", { className: "relative", children: [
              showTimeline && /* @__PURE__ */ jsx("div", { className: "border-border bg-surface-3 absolute top-6 left-[-26px] h-3 w-3 rounded-full border-2" }),
              /* @__PURE__ */ jsx(
                ActivityItem,
                {
                  type: activity.type,
                  message: activity.message,
                  timestamp: activity.timestamp,
                  details: activity.details,
                  onClick: () => onActivityClick?.(activity),
                  className: cn(
                    "rounded-none border-0",
                    index === 0 && "pt-4",
                    index === activities.length - 1 && "pb-4"
                  )
                }
              )
            ] }, activity.id)) }),
            hasMore && /* @__PURE__ */ jsx("div", { className: "border-border border-t p-4", children: /* @__PURE__ */ jsx(
              "button",
              {
                onClick: onLoadMore,
                disabled: loading,
                className: cn(
                  "text-primary w-full text-center text-base hover:underline",
                  "focus-ring disabled-state rounded disabled:cursor-not-allowed"
                ),
                children: loading ? "Loading..." : "Load more"
              }
            ) })
          ] }) })
        ]
      }
    );
  }
);
ActivityFeed.displayName = "ActivityFeed";
var chatPanelVariants = cva(
  "flex flex-col bg-surface-1 border border-border rounded-lg overflow-hidden",
  {
    variants: {
      size: {
        sm: "h-[400px]",
        md: "h-[500px]",
        lg: "h-[600px]",
        full: "h-full"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var ChatPanel = React17.forwardRef(
  ({
    className,
    size = "md",
    messages,
    isTyping = false,
    onSendMessage,
    onAttachFile,
    title = "Chat",
    subtitle,
    assistantAvatar = { fallback: "AI" },
    userAvatar = { fallback: "U" },
    placeholder = "Type a message...",
    disabled = false,
    onClose,
    ...props
  }, ref) => {
    const [inputValue, setInputValue] = React17.useState("");
    const messagesEndRef = React17.useRef(null);
    const inputRef = React17.useRef(null);
    React17.useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);
    const handleSend = () => {
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !disabled) {
        onSendMessage(trimmedValue);
        setInputValue("");
        inputRef.current?.focus();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    };
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(chatPanelVariants({ size }), className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-between border-b p-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
              /* @__PURE__ */ jsx(
                Avatar,
                {
                  size: "sm",
                  ...assistantAvatar.src && { src: assistantAvatar.src },
                  fallback: assistantAvatar.fallback ?? "AI"
                }
              ),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("h2", { className: "text-foreground font-medium", children: title }),
                subtitle && /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-sm", children: subtitle })
              ] })
            ] }),
            onClose && /* @__PURE__ */ jsx(
              IconButton,
              {
                variant: "ghost",
                size: "sm",
                onClick: onClose,
                "aria-label": "Close chat",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-4 overflow-y-auto p-4", tabIndex: 0, children: [
            messages.map((message) => {
              const avatarSrc = message.variant === "assistant" ? assistantAvatar.src : userAvatar.src;
              const avatarFallback = message.avatarFallback ?? (message.variant === "assistant" ? assistantAvatar.fallback : userAvatar.fallback);
              return /* @__PURE__ */ jsx(
                ChatBubble,
                {
                  variant: message.variant,
                  content: message.content,
                  timestamp: message.timestamp,
                  ...message.status && { status: message.status },
                  ...avatarSrc && { avatarSrc },
                  ...avatarFallback && { avatarFallback }
                },
                message.id
              );
            }),
            isTyping && /* @__PURE__ */ jsx(
              ChatBubble,
              {
                variant: "assistant",
                content: "",
                isTyping: true,
                ...assistantAvatar.src && { avatarSrc: assistantAvatar.src },
                ...assistantAvatar.fallback && {
                  avatarFallback: assistantAvatar.fallback
                },
                showTimestamp: false
              }
            ),
            /* @__PURE__ */ jsx("div", { ref: messagesEndRef })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "border-border border-t p-4", children: /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
            onAttachFile && /* @__PURE__ */ jsx(
              IconButton,
              {
                variant: "ghost",
                size: "sm",
                onClick: onAttachFile,
                "aria-label": "Attach file",
                disabled,
                children: /* @__PURE__ */ jsx(Paperclip, { className: "h-4 w-4" })
              }
            ),
            /* @__PURE__ */ jsx("div", { className: "relative flex-1", children: /* @__PURE__ */ jsx(
              "textarea",
              {
                ref: inputRef,
                value: inputValue,
                onChange: (e) => setInputValue(e.target.value),
                onKeyDown: handleKeyDown,
                placeholder,
                disabled,
                rows: 1,
                "aria-label": "Message input",
                className: cn(
                  "border-border bg-surface-2 text-foreground w-full resize-none rounded-lg border px-4 py-2.5 text-base",
                  "placeholder:text-foreground/40",
                  "focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  "max-h-32 overflow-y-auto"
                ),
                style: {
                  minHeight: "44px"
                }
              }
            ) }),
            /* @__PURE__ */ jsxs(
              Button,
              {
                onClick: handleSend,
                disabled: disabled || !inputValue.trim(),
                size: "default",
                className: "shrink-0",
                children: [
                  /* @__PURE__ */ jsx(Send, { className: "h-4 w-4" }),
                  /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Send message" })
                ]
              }
            )
          ] }) })
        ]
      }
    );
  }
);
ChatPanel.displayName = "ChatPanel";
var dataTableVariants = cva(
  "w-full bg-surface-1 rounded-lg overflow-hidden",
  {
    variants: {
      density: {
        compact: "",
        normal: "",
        comfortable: ""
      }
    },
    defaultVariants: {
      density: "normal"
    }
  }
);
var densityPadding = {
  compact: "px-3 py-1.5",
  normal: "px-4 py-2.5",
  comfortable: "px-4 py-4"
};
function DataTableInner({
  className,
  density = "normal",
  columns,
  data,
  loading = false,
  selectable = false,
  selectedIds = /* @__PURE__ */ new Set(),
  onSelect,
  sortKey,
  sortDirection,
  onSort,
  pagination,
  onPageChange,
  rowActions,
  onRowClick,
  emptyMessage = "No data available",
  getRowKey = (row) => row.id,
  ...props
}, ref) {
  const padding = densityPadding[density ?? "normal"];
  const handleSort = (key) => {
    if (!onSort) return;
    let newDirection = "asc";
    if (sortKey === key) {
      if (sortDirection === "asc") newDirection = "desc";
      else if (sortDirection === "desc") newDirection = null;
    }
    onSort(key, newDirection);
  };
  const handleSelectAll = (checked) => {
    if (!onSelect) return;
    if (checked) {
      onSelect(new Set(data.map(getRowKey)));
    } else {
      onSelect(/* @__PURE__ */ new Set());
    }
  };
  const handleRowSelect = (rowId, checked) => {
    if (!onSelect) return;
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    onSelect(newSelected);
  };
  const allSelected = data.length > 0 && data.every((row) => selectedIds.has(getRowKey(row)));
  const someSelected = data.some((row) => selectedIds.has(getRowKey(row)));
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 1;
  const startItem = pagination ? (pagination.page - 1) * pagination.pageSize + 1 : 1;
  const endItem = pagination ? Math.min(pagination.page * pagination.pageSize, pagination.total) : data.length;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref,
      className: cn(dataTableVariants({ density }), className),
      ...props,
      children: [
        /* @__PURE__ */ jsx("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", { className: "w-full", children: [
          /* @__PURE__ */ jsx("thead", { className: "border-border bg-surface-2 border-b", children: /* @__PURE__ */ jsxs("tr", { children: [
            selectable && /* @__PURE__ */ jsx("th", { className: cn(padding, "w-10"), children: /* @__PURE__ */ jsx(
              Checkbox,
              {
                checked: allSelected,
                indeterminate: someSelected && !allSelected,
                onCheckedChange: (checked) => handleSelectAll(checked === true),
                "aria-label": "Select all rows"
              }
            ) }),
            columns.map((column) => {
              const ariaSortValue = column.sortable ? sortKey === column.key ? sortDirection === "asc" ? "ascending" : sortDirection === "desc" ? "descending" : "none" : "none" : void 0;
              return /* @__PURE__ */ jsx(
                "th",
                {
                  className: cn(
                    padding,
                    "text-foreground/60 text-left text-sm font-medium tracking-wider uppercase",
                    column.align === "center" && "text-center",
                    column.align === "right" && "text-right",
                    column.sortable && "hover:text-foreground cursor-pointer"
                  ),
                  style: { width: column.width },
                  onClick: () => column.sortable && handleSort(column.key),
                  ...ariaSortValue && { "aria-sort": ariaSortValue },
                  ...column.sortable && { role: "columnheader" },
                  children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                    /* @__PURE__ */ jsx("span", { children: column.header }),
                    column.sortable && /* @__PURE__ */ jsx("span", { className: "ml-1", children: sortKey === column.key ? sortDirection === "asc" ? /* @__PURE__ */ jsx(ArrowUp, { className: "h-3 w-3" }) : sortDirection === "desc" ? /* @__PURE__ */ jsx(ArrowDown, { className: "h-3 w-3" }) : /* @__PURE__ */ jsx(ArrowUpDown, { className: "h-3 w-3 opacity-50" }) : /* @__PURE__ */ jsx(ArrowUpDown, { className: "h-3 w-3 opacity-50" }) })
                  ] })
                },
                column.key
              );
            }),
            rowActions && /* @__PURE__ */ jsx("th", { className: cn(padding, "w-10"), children: /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Actions" }) })
          ] }) }),
          /* @__PURE__ */ jsx("tbody", { className: "divide-border divide-y", children: loading ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx(
            "td",
            {
              colSpan: columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0),
              className: "py-12 text-center",
              children: /* @__PURE__ */ jsx(Spinner, { size: "lg" })
            }
          ) }) : data.length === 0 ? /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx(
            "td",
            {
              colSpan: columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0),
              className: "text-foreground/60 py-12 text-center",
              children: emptyMessage
            }
          ) }) : data.map((row, rowIndex) => {
            const rowKey = getRowKey(row);
            const isSelected = selectedIds.has(rowKey);
            return /* @__PURE__ */ jsxs(
              "tr",
              {
                className: cn(
                  "transition-colors duration-150",
                  isSelected && "bg-primary/5",
                  onRowClick && "hover:bg-surface-1 cursor-pointer"
                ),
                onClick: () => onRowClick?.(row),
                children: [
                  selectable && /* @__PURE__ */ jsx(
                    "td",
                    {
                      className: padding,
                      onClick: (e) => e.stopPropagation(),
                      children: /* @__PURE__ */ jsx(
                        Checkbox,
                        {
                          checked: isSelected,
                          onCheckedChange: (checked) => handleRowSelect(rowKey, checked === true),
                          "aria-label": `Select row ${rowIndex + 1}`
                        }
                      )
                    }
                  ),
                  columns.map((column) => /* @__PURE__ */ jsx(
                    "td",
                    {
                      className: cn(
                        padding,
                        "text-foreground text-base",
                        column.align === "center" && "text-center",
                        column.align === "right" && "text-right"
                      ),
                      children: column.render ? column.render(row, rowIndex) : row[column.key]
                    },
                    column.key
                  )),
                  rowActions && /* @__PURE__ */ jsx(
                    "td",
                    {
                      className: padding,
                      onClick: (e) => e.stopPropagation(),
                      children: rowActions(row)
                    }
                  )
                ]
              },
              rowKey
            );
          }) })
        ] }) }),
        pagination && /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-between border-t px-4 py-3", children: [
          /* @__PURE__ */ jsxs("p", { className: "text-foreground/60 text-base", children: [
            "Showing ",
            startItem,
            "-",
            endItem,
            " of ",
            pagination.total
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
            /* @__PURE__ */ jsx(
              IconButton,
              {
                variant: "ghost",
                size: "sm",
                onClick: () => onPageChange?.(pagination.page - 1),
                disabled: pagination.page <= 1,
                "aria-label": "Previous page",
                children: /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" })
              }
            ),
            /* @__PURE__ */ jsxs("span", { className: "text-foreground px-3 text-base", children: [
              "Page ",
              pagination.page,
              " of ",
              totalPages
            ] }),
            /* @__PURE__ */ jsx(
              IconButton,
              {
                variant: "ghost",
                size: "sm",
                onClick: () => onPageChange?.(pagination.page + 1),
                disabled: pagination.page >= totalPages,
                "aria-label": "Next page",
                children: /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
              }
            )
          ] })
        ] })
      ]
    }
  );
}
var DataTable = React17.forwardRef(DataTableInner);
var notificationTrayVariants = cva(
  "flex flex-col bg-surface-2 border border-border rounded-lg shadow-lg overflow-hidden",
  {
    variants: {
      size: {
        sm: "w-80 max-h-[400px]",
        md: "w-96 max-h-[500px]",
        lg: "w-[420px] max-h-[600px]"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var formatRelativeTime2 = (date) => {
  const now = /* @__PURE__ */ new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 6e4);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};
var NotificationTray = React17.forwardRef(
  ({
    className,
    size = "md",
    notifications,
    onNotificationClick,
    onMarkAsRead,
    onDelete,
    onMarkAllAsRead,
    onSettingsClick,
    onClose,
    emptyMessage = "No notifications",
    ...props
  }, ref) => {
    const unreadCount = notifications.filter((n) => !n.read).length;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(notificationTrayVariants({ size }), className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-between border-b p-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(Bell, { className: "text-foreground h-5 w-5" }),
              /* @__PURE__ */ jsx("h2", { className: "text-foreground font-medium", children: "Notifications" }),
              unreadCount > 0 && /* @__PURE__ */ jsx("span", { className: "bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-sm font-medium", children: unreadCount })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
              unreadCount > 0 && onMarkAllAsRead && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onMarkAllAsRead,
                  "aria-label": "Mark all as read",
                  children: /* @__PURE__ */ jsx(CheckCheck, { className: "h-4 w-4" })
                }
              ),
              onSettingsClick && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onSettingsClick,
                  "aria-label": "Notification settings",
                  children: /* @__PURE__ */ jsx(Settings, { className: "h-4 w-4" })
                }
              ),
              onClose && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onClose,
                  "aria-label": "Close notifications",
                  children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto", tabIndex: 0, children: notifications.length === 0 ? /* @__PURE__ */ jsxs("div", { className: "text-foreground/60 flex flex-col items-center justify-center py-12", children: [
            /* @__PURE__ */ jsx(Bell, { className: "mb-3 h-12 w-12 opacity-50" }),
            /* @__PURE__ */ jsx("p", { className: "text-base", children: emptyMessage })
          ] }) : /* @__PURE__ */ jsx("ul", { className: "divide-border divide-y", children: notifications.map((notification) => /* @__PURE__ */ jsxs(
            "li",
            {
              className: cn(
                "group relative",
                !notification.read && "bg-primary/5"
              ),
              children: [
                /* @__PURE__ */ jsxs(
                  "button",
                  {
                    className: "hover:bg-surface-3 focus-visible:ring-ring w-full p-4 text-left transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset",
                    onClick: () => onNotificationClick?.(notification),
                    children: [
                      !notification.read && /* @__PURE__ */ jsx("span", { className: "bg-primary absolute top-1/2 left-2 h-2 w-2 -translate-y-1/2 rounded-full" }),
                      /* @__PURE__ */ jsxs("div", { className: "pl-4", children: [
                        /* @__PURE__ */ jsx("p", { className: "text-foreground pr-16 text-base font-medium", children: notification.title }),
                        notification.description && /* @__PURE__ */ jsx("p", { className: "text-foreground/60 mt-1 line-clamp-2 text-base", children: notification.description }),
                        /* @__PURE__ */ jsx("p", { className: "text-foreground/60 mt-1 text-sm", children: formatRelativeTime2(notification.timestamp) })
                      ] })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "absolute top-2 right-2 flex items-center gap-1 opacity-0 transition-opacity duration-100 group-hover:opacity-100", children: [
                  !notification.read && onMarkAsRead && /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      variant: "ghost",
                      size: "sm",
                      onClick: (e) => {
                        e.stopPropagation();
                        onMarkAsRead(notification.id);
                      },
                      "aria-label": "Mark as read",
                      children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" })
                    }
                  ),
                  onDelete && /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      variant: "ghost",
                      size: "sm",
                      onClick: (e) => {
                        e.stopPropagation();
                        onDelete(notification.id);
                      },
                      "aria-label": "Delete notification",
                      children: /* @__PURE__ */ jsx(Trash2, { className: "h-4 w-4" })
                    }
                  )
                ] })
              ]
            },
            notification.id
          )) }) })
        ]
      }
    );
  }
);
NotificationTray.displayName = "NotificationTray";
var projectCardVariants = cva(
  "rounded-lg border border-border bg-surface-1 overflow-hidden transition-colors duration-150",
  {
    variants: {
      interactive: {
        true: "cursor-pointer hover:bg-surface-2 hover:border-border",
        false: ""
      },
      layout: {
        vertical: "flex flex-col",
        horizontal: "flex flex-row"
      }
    },
    defaultVariants: {
      interactive: true,
      layout: "vertical"
    }
  }
);
var statusConfig = {
  active: { label: "Active", variant: "default" },
  "on-hold": { label: "On Hold", variant: "secondary" },
  completed: { label: "Completed", variant: "default" },
  archived: { label: "Archived", variant: "outline" }
};
var requestStatusConfig = [
  {
    key: "draft",
    label: "Draft",
    bgClass: "bg-text-secondary"
  },
  {
    key: "pending",
    label: "Pending",
    bgClass: "bg-warning"
  },
  {
    key: "inProgress",
    label: "Active",
    bgClass: "bg-info"
  },
  {
    key: "review",
    label: "Review",
    bgClass: "bg-primary"
  },
  {
    key: "completed",
    label: "Done",
    bgClass: "bg-success"
  }
];
var formatRelativeTime3 = (date) => {
  const now = /* @__PURE__ */ new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 6e4);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};
var ProjectCard = React17.forwardRef(
  ({
    className,
    interactive = true,
    layout: layout2 = "vertical",
    name,
    description,
    status,
    coverImage,
    icon,
    progress,
    requestCount,
    requestSummary,
    members,
    lastUpdated,
    onCardClick,
    onMoreClick,
    ...props
  }, ref) => {
    const statusInfo = statusConfig[status];
    const displayMembers = members?.slice(0, 3);
    const remainingMembers = members ? members.length - 3 : 0;
    const handleKeyDown = (event) => {
      if (interactive && onCardClick && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onCardClick();
      }
    };
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(projectCardVariants({ interactive, layout: layout2 }), className),
        onClick: interactive ? onCardClick : void 0,
        onKeyDown: handleKeyDown,
        tabIndex: interactive && onCardClick ? 0 : void 0,
        role: interactive && onCardClick ? "button" : void 0,
        "aria-label": interactive && onCardClick ? `${name} project` : void 0,
        ...props,
        children: [
          coverImage && layout2 === "vertical" && /* @__PURE__ */ jsx("div", { className: "bg-surface-3 h-32 overflow-hidden", children: /* @__PURE__ */ jsx(
            "img",
            {
              src: coverImage,
              alt: "",
              className: "h-full w-full object-cover"
            }
          ) }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn("flex-1 p-5", layout2 === "horizontal" && "flex gap-4"),
              children: [
                layout2 === "horizontal" && coverImage && /* @__PURE__ */ jsx("div", { className: "bg-surface-3 h-24 w-24 shrink-0 overflow-hidden rounded-md", children: /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: coverImage,
                    alt: "",
                    className: "h-full w-full object-cover"
                  }
                ) }),
                /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
                  /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-start justify-between gap-2", children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [
                      icon ?? /* @__PURE__ */ jsx(Folder, { className: "text-primary h-5 w-5 shrink-0" }),
                      /* @__PURE__ */ jsx("h3", { className: "text-foreground truncate font-medium", children: name })
                    ] }),
                    /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [
                      /* @__PURE__ */ jsx(Badge, { variant: statusInfo.variant, children: statusInfo.label }),
                      onMoreClick && /* @__PURE__ */ jsx(
                        IconButton,
                        {
                          variant: "ghost",
                          size: "sm",
                          onClick: (e) => {
                            e.stopPropagation();
                            onMoreClick(e);
                          },
                          "aria-label": "More actions",
                          children: /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" })
                        }
                      )
                    ] })
                  ] }),
                  description && /* @__PURE__ */ jsx("p", { className: "text-foreground/70 mb-3 line-clamp-2 text-base", children: description }),
                  progress !== void 0 && /* @__PURE__ */ jsxs("div", { className: "mb-3", children: [
                    /* @__PURE__ */ jsxs("div", { className: "text-foreground/60 mb-1 flex items-center justify-between text-base", children: [
                      /* @__PURE__ */ jsx("span", { children: "Progress" }),
                      /* @__PURE__ */ jsxs("span", { children: [
                        progress,
                        "%"
                      ] })
                    ] }),
                    /* @__PURE__ */ jsx(Progress, { value: progress, className: "h-1.5" })
                  ] }),
                  requestSummary && (() => {
                    const total = Object.values(requestSummary).reduce(
                      (a, b) => a + b,
                      0
                    );
                    const activeStatuses = requestStatusConfig.filter(
                      (s) => requestSummary[s.key] > 0
                    );
                    if (total === 0) {
                      return /* @__PURE__ */ jsxs("div", { className: "text-foreground/60 mb-3 flex items-center gap-1.5 text-base", children: [
                        /* @__PURE__ */ jsx(FileText, { className: "h-4 w-4" }),
                        /* @__PURE__ */ jsx("span", { children: "No requests yet" })
                      ] });
                    }
                    return /* @__PURE__ */ jsxs("div", { className: "mb-3", children: [
                      /* @__PURE__ */ jsx("div", { className: "bg-surface-3 flex h-1.5 w-full overflow-hidden rounded-full", children: requestStatusConfig.map((s) => {
                        const count = requestSummary[s.key];
                        if (count === 0) return null;
                        const pct = count / total * 100;
                        return /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: cn("h-full", s.bgClass),
                            style: { width: `${pct}%` },
                            title: `${count} ${s.label}`
                          },
                          s.key
                        );
                      }) }),
                      /* @__PURE__ */ jsx("div", { className: "mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5", children: activeStatuses.map((s) => /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "text-foreground/60 flex items-center gap-1 text-base",
                          children: [
                            /* @__PURE__ */ jsx(
                              "span",
                              {
                                className: cn(
                                  "inline-block h-1.5 w-1.5 rounded-full",
                                  s.bgClass
                                )
                              }
                            ),
                            /* @__PURE__ */ jsxs("span", { children: [
                              requestSummary[s.key],
                              " ",
                              s.label
                            ] })
                          ]
                        },
                        s.key
                      )) })
                    ] });
                  })(),
                  /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-between border-t pt-3", children: [
                    /* @__PURE__ */ jsxs("div", { className: "text-foreground/60 flex items-center gap-4 text-base", children: [
                      requestCount !== void 0 && !requestSummary && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                        /* @__PURE__ */ jsx(FileText, { className: "h-4 w-4" }),
                        /* @__PURE__ */ jsx("span", { children: requestCount })
                      ] }),
                      requestSummary && (() => {
                        const total = Object.values(requestSummary).reduce(
                          (a, b) => a + b,
                          0
                        );
                        if (total === 0) return null;
                        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                          /* @__PURE__ */ jsx(FileText, { className: "h-4 w-4" }),
                          /* @__PURE__ */ jsxs("span", { children: [
                            total,
                            " requests"
                          ] })
                        ] });
                      })(),
                      members && members.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                        /* @__PURE__ */ jsx(Users, { className: "h-4 w-4" }),
                        /* @__PURE__ */ jsx("span", { children: members.length })
                      ] }),
                      lastUpdated && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
                        /* @__PURE__ */ jsx(Calendar, { className: "h-3.5 w-3.5" }),
                        /* @__PURE__ */ jsx("span", { className: "text-base", children: formatRelativeTime3(lastUpdated) })
                      ] })
                    ] }),
                    displayMembers && displayMembers.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex items-center", children: /* @__PURE__ */ jsxs("div", { className: "flex -space-x-2", children: [
                      displayMembers.map((member, index) => /* @__PURE__ */ jsx(
                        Avatar,
                        {
                          size: "sm",
                          ...member.avatarSrc && { src: member.avatarSrc },
                          fallback: member.name,
                          className: "ring-surface-1 ring-2"
                        },
                        index
                      )),
                      remainingMembers > 0 && /* @__PURE__ */ jsxs("div", { className: "bg-surface-3 text-foreground/60 ring-surface-1 flex h-6 w-6 items-center justify-center rounded-full text-base ring-2", children: [
                        "+",
                        remainingMembers
                      ] })
                    ] }) })
                  ] })
                ] })
              ]
            }
          )
        ]
      }
    );
  }
);
ProjectCard.displayName = "ProjectCard";
var requestCardVariants = cva(
  "rounded-lg border border-border bg-surface-1 p-5 transition-colors duration-150",
  {
    variants: {
      interactive: {
        true: "cursor-pointer hover:bg-surface-2 hover:border-border",
        false: ""
      }
    },
    defaultVariants: {
      interactive: true
    }
  }
);
var statusConfig2 = {
  draft: { label: "Draft", variant: "secondary" },
  pending: { label: "Pending", variant: "outline" },
  "in-progress": { label: "In Progress", variant: "default" },
  review: { label: "Review", variant: "secondary" },
  completed: { label: "Completed", variant: "default" },
  cancelled: { label: "Cancelled", variant: "destructive" }
};
var priorityConfig = {
  low: {
    label: "Low",
    className: "bg-muted text-foreground/60"
  },
  medium: {
    label: "Medium",
    className: "bg-warning text-text-inverse"
  },
  high: {
    label: "High",
    className: "bg-error text-text-inverse"
  },
  urgent: {
    label: "Urgent",
    className: "bg-error text-text-inverse animate-pulse"
  }
};
var formatRelativeDate = (date) => {
  const now = /* @__PURE__ */ new Date();
  const diff = date.getTime() - now.getTime();
  const days = Math.ceil(diff / (1e3 * 60 * 60 * 24));
  if (days < 0) {
    return `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} overdue`;
  }
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days} days`;
};
var RequestCard = React17.forwardRef(
  ({
    className,
    interactive = true,
    title,
    description,
    status,
    priority,
    assignee,
    dueDate,
    requestId,
    tags,
    onCardClick,
    onMoreClick,
    onExternalClick,
    ...props
  }, ref) => {
    const statusInfo = statusConfig2[status];
    const priorityInfo = priority ? priorityConfig[priority] : null;
    const handleKeyDown = (event) => {
      if (interactive && onCardClick && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onCardClick();
      }
    };
    const titleText = typeof title === "string" ? title : requestId ?? "Request";
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(requestCardVariants({ interactive }), className),
        onClick: interactive ? onCardClick : void 0,
        onKeyDown: handleKeyDown,
        tabIndex: interactive && onCardClick ? 0 : void 0,
        role: interactive && onCardClick ? "button" : void 0,
        "aria-label": interactive && onCardClick ? `${titleText} request card` : void 0,
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-start justify-between gap-3", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
              /* @__PURE__ */ jsx(Badge, { variant: statusInfo.variant, children: statusInfo.label }),
              priorityInfo && /* @__PURE__ */ jsx(
                "span",
                {
                  className: cn(
                    "rounded-full px-2 py-0.5 text-base font-medium",
                    priorityInfo.className
                  ),
                  children: priorityInfo.label
                }
              )
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
              onExternalClick && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: (e) => {
                    e.stopPropagation();
                    onExternalClick(e);
                  },
                  "aria-label": "Open in new tab",
                  children: /* @__PURE__ */ jsx(ExternalLink, { className: "h-4 w-4" })
                }
              ),
              onMoreClick && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: (e) => {
                    e.stopPropagation();
                    onMoreClick(e);
                  },
                  "aria-label": "More actions",
                  children: /* @__PURE__ */ jsx(MoreHorizontal, { className: "h-4 w-4" })
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mb-2", children: [
            requestId && /* @__PURE__ */ jsx("span", { className: "text-foreground/50 font-mono text-base", children: requestId }),
            /* @__PURE__ */ jsx("h3", { className: "text-foreground line-clamp-2 font-medium", children: title })
          ] }),
          description && /* @__PURE__ */ jsx("p", { className: "text-foreground/70 mb-3 line-clamp-2 text-base", children: description }),
          tags && tags.length > 0 && /* @__PURE__ */ jsx("div", { className: "mb-3 flex flex-wrap gap-1", children: tags.map((tag, index) => /* @__PURE__ */ jsx(
            "span",
            {
              className: "bg-surface-3 text-foreground/60 rounded px-2 py-0.5 text-base",
              children: tag
            },
            index
          )) }),
          /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-between border-t pt-3", children: [
            assignee ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(
                Avatar,
                {
                  size: "sm",
                  ...assignee.avatarSrc && { src: assignee.avatarSrc },
                  fallback: assignee.name
                }
              ),
              /* @__PURE__ */ jsx("span", { className: "text-foreground/70 text-base", children: assignee.name })
            ] }) : /* @__PURE__ */ jsxs("div", { className: "text-foreground/50 flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(User, { className: "h-4 w-4" }),
              /* @__PURE__ */ jsx("span", { className: "text-base", children: "Unassigned" })
            ] }),
            dueDate && /* @__PURE__ */ jsxs("div", { className: "text-foreground/60 flex items-center gap-1", children: [
              /* @__PURE__ */ jsx(Clock, { className: "h-4 w-4" }),
              /* @__PURE__ */ jsx("span", { className: "text-base", children: formatRelativeDate(dueDate) })
            ] })
          ] })
        ]
      }
    );
  }
);
RequestCard.displayName = "RequestCard";
var reviewPanelVariants = cva(
  "flex flex-col bg-surface-1 border border-border rounded-lg overflow-hidden",
  {
    variants: {
      size: {
        sm: "max-h-[400px]",
        md: "max-h-[500px]",
        lg: "max-h-[600px]",
        full: "h-full"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var statusConfig3 = {
  pending: { label: "Pending", variant: "outline" },
  approved: { label: "Approved", variant: "default" },
  changes_requested: { label: "Changes Requested", variant: "destructive" },
  commented: { label: "Commented", variant: "secondary" }
};
var formatRelativeTime4 = (date) => {
  const now = /* @__PURE__ */ new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 6e4);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};
var ReviewPanel = React17.forwardRef(
  ({
    className,
    size = "md",
    items,
    title = "Review",
    reviewer,
    onApprove,
    onRequestChanges,
    onAddComment,
    onResolveComment,
    onClose,
    disabled = false,
    ...props
  }, ref) => {
    const [expandedItems, setExpandedItems] = React17.useState(
      /* @__PURE__ */ new Set()
    );
    const [commentInputs, setCommentInputs] = React17.useState({});
    const toggleItem = (id) => {
      setExpandedItems((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    };
    const handleAddComment = (itemId) => {
      const comment = commentInputs[itemId]?.trim();
      if (comment && onAddComment) {
        onAddComment(itemId, comment);
        setCommentInputs((prev) => ({ ...prev, [itemId]: "" }));
      }
    };
    const pendingCount = items.filter((i) => i.status === "pending").length;
    const approvedCount = items.filter((i) => i.status === "approved").length;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(reviewPanelVariants({ size }), className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-between border-b p-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
              /* @__PURE__ */ jsx(MessageSquare, { className: "text-foreground h-5 w-5" }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsx("h2", { className: "text-foreground font-medium", children: title }),
                /* @__PURE__ */ jsxs("p", { className: "text-foreground/60 text-sm", children: [
                  approvedCount,
                  "/",
                  items.length,
                  " approved"
                ] })
              ] })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              reviewer && /* @__PURE__ */ jsx(
                Avatar,
                {
                  size: "sm",
                  ...reviewer.avatarSrc && { src: reviewer.avatarSrc },
                  fallback: reviewer.name
                }
              ),
              onClose && /* @__PURE__ */ jsx(
                IconButton,
                {
                  variant: "ghost",
                  size: "sm",
                  onClick: onClose,
                  "aria-label": "Close panel",
                  children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto", tabIndex: 0, children: items.map((item) => {
            const isExpanded = expandedItems.has(item.id);
            const statusInfo = statusConfig3[item.status];
            const unresolvedComments = item.comments.filter(
              (c) => !c.resolved
            ).length;
            return /* @__PURE__ */ jsxs(
              "div",
              {
                className: "border-border border-b last:border-b-0",
                children: [
                  /* @__PURE__ */ jsxs(
                    "button",
                    {
                      onClick: () => toggleItem(item.id),
                      className: "hover:bg-surface-2 focus-visible:ring-ring flex w-full items-center justify-between p-4 text-left transition-colors duration-100 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
                      children: [
                        /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-3", children: [
                          item.status === "approved" ? /* @__PURE__ */ jsx(CheckCircle, { className: "text-success h-5 w-5 shrink-0" }) : item.status === "changes_requested" ? /* @__PURE__ */ jsx(XCircle, { className: "text-error h-5 w-5 shrink-0" }) : /* @__PURE__ */ jsx("div", { className: "border-border h-5 w-5 shrink-0 rounded-full border-2" }),
                          /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
                            /* @__PURE__ */ jsx("p", { className: "text-foreground truncate font-medium", children: item.title }),
                            item.description && /* @__PURE__ */ jsx("p", { className: "text-foreground/60 truncate text-base", children: item.description })
                          ] })
                        ] }),
                        /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [
                          /* @__PURE__ */ jsx(Badge, { variant: statusInfo.variant, children: statusInfo.label }),
                          unresolvedComments > 0 && /* @__PURE__ */ jsxs("span", { className: "text-foreground/60 text-sm", children: [
                            unresolvedComments,
                            " comment",
                            unresolvedComments !== 1 ? "s" : ""
                          ] }),
                          isExpanded ? /* @__PURE__ */ jsx(ChevronUp, { className: "text-foreground/60 h-4 w-4" }) : /* @__PURE__ */ jsx(ChevronDown, { className: "text-foreground/60 h-4 w-4" })
                        ] })
                      ]
                    }
                  ),
                  isExpanded && /* @__PURE__ */ jsxs("div", { className: "bg-surface-2 px-4 pb-4", children: [
                    item.comments.length > 0 && /* @__PURE__ */ jsx("div", { className: "mb-4 space-y-3", children: item.comments.map((comment) => /* @__PURE__ */ jsxs(
                      "div",
                      {
                        className: cn(
                          "bg-surface-1 rounded-lg p-3",
                          comment.resolved && "opacity-50"
                        ),
                        children: [
                          /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center justify-between", children: [
                            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                              /* @__PURE__ */ jsx(
                                Avatar,
                                {
                                  size: "sm",
                                  ...comment.author.avatarSrc && {
                                    src: comment.author.avatarSrc
                                  },
                                  fallback: comment.author.name
                                }
                              ),
                              /* @__PURE__ */ jsx("span", { className: "text-foreground text-base font-medium", children: comment.author.name }),
                              /* @__PURE__ */ jsx("span", { className: "text-foreground/60 text-sm", children: formatRelativeTime4(comment.timestamp) })
                            ] }),
                            !comment.resolved && onResolveComment && /* @__PURE__ */ jsx(
                              "button",
                              {
                                onClick: () => onResolveComment(item.id, comment.id),
                                className: "text-primary text-sm hover:underline",
                                children: "Resolve"
                              }
                            )
                          ] }),
                          /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: comment.content })
                        ]
                      },
                      comment.id
                    )) }),
                    onAddComment && /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
                      /* @__PURE__ */ jsx(
                        TextArea,
                        {
                          value: commentInputs[item.id] ?? "",
                          onChange: (e) => setCommentInputs((prev) => ({
                            ...prev,
                            [item.id]: e.target.value
                          })),
                          placeholder: "Add a comment...",
                          rows: 2,
                          disabled
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Button,
                        {
                          size: "sm",
                          onClick: () => handleAddComment(item.id),
                          disabled: disabled || !commentInputs[item.id]?.trim(),
                          children: "Comment"
                        }
                      )
                    ] })
                  ] })
                ]
              },
              item.id
            );
          }) }),
          (onApprove || onRequestChanges) && /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center justify-end gap-2 border-t p-4", children: [
            onRequestChanges && /* @__PURE__ */ jsx(
              Button,
              {
                variant: "outline",
                onClick: onRequestChanges,
                disabled,
                children: "Request Changes"
              }
            ),
            onApprove && /* @__PURE__ */ jsx(
              Button,
              {
                onClick: onApprove,
                disabled: disabled || pendingCount > 0,
                children: "Approve"
              }
            )
          ] })
        ]
      }
    );
  }
);
ReviewPanel.displayName = "ReviewPanel";
var stepIndicatorVariants = cva(
  "flex items-center justify-center rounded-full font-medium transition-colors duration-150 shrink-0",
  {
    variants: {
      status: {
        completed: "bg-success text-primary-foreground",
        active: "bg-primary text-primary-foreground",
        upcoming: "bg-surface-3 text-foreground/60 border-2 border-border"
      },
      size: {
        sm: "h-6 w-6 text-sm",
        md: "h-8 w-8 text-base",
        lg: "h-10 w-10 text-base"
      }
    },
    defaultVariants: {
      status: "upcoming",
      size: "md"
    }
  }
);
var WizardStep = React17.forwardRef(
  ({
    className,
    size = "md",
    steps,
    activeStep,
    orientation = "horizontal",
    onStepClick,
    allowStepClick = true,
    showNumbers = true,
    ...props
  }, ref) => {
    const getStepStatus = (index) => {
      if (index < activeStep) return "completed";
      if (index === activeStep) return "active";
      return "upcoming";
    };
    const handleStepClick = (index) => {
      if (!onStepClick) return;
      const status = getStepStatus(index);
      if (allowStepClick && (status === "completed" || status === "active")) {
        onStepClick(index);
      }
    };
    const isHorizontal = orientation === "horizontal";
    if (isHorizontal) {
      return /* @__PURE__ */ jsxs(
        "div",
        {
          ref,
          className: cn("flex flex-col", className),
          role: "list",
          "aria-label": "Wizard steps",
          ...props,
          children: [
            /* @__PURE__ */ jsx("div", { className: "flex", children: steps.map((step, index) => {
              const status = getStepStatus(index);
              const isFirst = index === 0;
              const isLast = index === steps.length - 1;
              const isClickable = allowStepClick && (status === "completed" || status === "active");
              const prevStatus = index > 0 ? getStepStatus(index - 1) : null;
              return /* @__PURE__ */ jsxs("div", { className: "flex flex-1 items-center", children: [
                isFirst ? /* @__PURE__ */ jsx("div", { className: "flex-1", "aria-hidden": "true" }) : /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "h-0.5 flex-1",
                      prevStatus === "completed" ? "bg-success" : "bg-border"
                    ),
                    "aria-hidden": "true"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: () => handleStepClick(index),
                    disabled: !isClickable,
                    className: cn(
                      stepIndicatorVariants({ status, size }),
                      isClickable ? "cursor-pointer" : "cursor-default",
                      "focus-visible:ring-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    ),
                    "aria-current": status === "active" ? "step" : void 0,
                    "aria-label": `Step ${index + 1}: ${step.title}${status === "completed" ? " (completed)" : ""}`,
                    role: "listitem",
                    children: status === "completed" ? /* @__PURE__ */ jsx(
                      Check,
                      {
                        className: cn(
                          size === "sm" && "h-3 w-3",
                          size === "md" && "h-4 w-4",
                          size === "lg" && "h-5 w-5"
                        )
                      }
                    ) : step.icon && !showNumbers ? /* @__PURE__ */ jsx(
                      step.icon,
                      {
                        className: cn(
                          size === "sm" && "h-3 w-3",
                          size === "md" && "h-4 w-4",
                          size === "lg" && "h-5 w-5"
                        )
                      }
                    ) : index + 1
                  }
                ),
                isLast ? /* @__PURE__ */ jsx("div", { className: "flex-1", "aria-hidden": "true" }) : /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "h-0.5 flex-1",
                      status === "completed" ? "bg-success" : "bg-border"
                    ),
                    "aria-hidden": "true"
                  }
                )
              ] }, step.id);
            }) }),
            /* @__PURE__ */ jsx("div", { className: "mt-2 flex gap-4", children: steps.map((step, index) => {
              const status = getStepStatus(index);
              return /* @__PURE__ */ jsxs("div", { className: "flex-1 px-2 text-center", children: [
                /* @__PURE__ */ jsxs(
                  "p",
                  {
                    className: cn(
                      "text-base font-medium",
                      status === "active" ? "text-foreground" : status === "completed" ? "text-foreground/60" : "text-foreground/60"
                    ),
                    children: [
                      step.title,
                      step.optional && /* @__PURE__ */ jsx("span", { className: "text-foreground/60 ml-1 text-sm font-normal", children: "(Optional)" })
                    ]
                  }
                ),
                step.description && /* @__PURE__ */ jsx(
                  "p",
                  {
                    className: cn(
                      "mt-0.5 text-sm",
                      status === "active" ? "text-foreground/60" : "text-foreground/60"
                    ),
                    children: step.description
                  }
                )
              ] }, step.id);
            }) })
          ]
        }
      );
    }
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("flex flex-col", className),
        role: "list",
        "aria-label": "Wizard steps",
        ...props,
        children: steps.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === steps.length - 1;
          const isClickable = allowStepClick && (status === "completed" || status === "active");
          return /* @__PURE__ */ jsxs(
            "div",
            {
              className: "flex flex-row items-start",
              role: "listitem",
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center", children: [
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      onClick: () => handleStepClick(index),
                      disabled: !isClickable,
                      className: cn(
                        stepIndicatorVariants({ status, size }),
                        isClickable ? "cursor-pointer" : "cursor-default",
                        "focus-visible:ring-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      ),
                      "aria-current": status === "active" ? "step" : void 0,
                      "aria-label": `Step ${index + 1}: ${step.title}${status === "completed" ? " (completed)" : ""}`,
                      children: status === "completed" ? /* @__PURE__ */ jsx(
                        Check,
                        {
                          className: cn(
                            size === "sm" && "h-3 w-3",
                            size === "md" && "h-4 w-4",
                            size === "lg" && "h-5 w-5"
                          )
                        }
                      ) : step.icon && !showNumbers ? /* @__PURE__ */ jsx(
                        step.icon,
                        {
                          className: cn(
                            size === "sm" && "h-3 w-3",
                            size === "md" && "h-4 w-4",
                            size === "lg" && "h-5 w-5"
                          )
                        }
                      ) : index + 1
                    }
                  ),
                  !isLast && /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: cn(
                        "my-2 h-full min-h-[24px] w-0.5",
                        status === "completed" ? "bg-success" : "bg-border"
                      ),
                      "aria-hidden": "true"
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "ml-4 pb-8", children: [
                  /* @__PURE__ */ jsxs(
                    "p",
                    {
                      className: cn(
                        "text-base font-medium",
                        status === "active" ? "text-foreground" : status === "completed" ? "text-foreground/60" : "text-foreground/60"
                      ),
                      children: [
                        step.title,
                        step.optional && /* @__PURE__ */ jsx("span", { className: "text-foreground/60 ml-1 text-sm font-normal", children: "(Optional)" })
                      ]
                    }
                  ),
                  step.description && /* @__PURE__ */ jsx(
                    "p",
                    {
                      className: cn(
                        "mt-0.5 text-sm",
                        status === "active" ? "text-foreground/60" : "text-foreground/60"
                      ),
                      children: step.description
                    }
                  )
                ] })
              ]
            },
            step.id
          );
        })
      }
    );
  }
);
WizardStep.displayName = "WizardStep";
var agentChatPanelVariants = cva(
  "relative flex h-full w-full flex-col overflow-hidden bg-surface-1"
);
async function fetchPersistedHistory(sessionId, fetchWithAuth, setMessages) {
  try {
    const response = await fetchWithAuth(`/sessions/${sessionId}/messages`);
    if (!response.ok) return;
    const body = await response.json();
    const messages = body?.data?.messages;
    if (!Array.isArray(messages) || messages.length === 0) return;
    setMessages(
      messages.map(
        (m) => ({
          ...m,
          isStreaming: false,
          toolCalls: (m.toolCalls ?? []).map((tc) => ({
            ...tc,
            isExecuting: false
          }))
        })
      )
    );
  } catch {
  }
}
function AgentChatPanel({
  websocketUrl,
  sessionStatus,
  elementContext,
  onElementContextConsumed,
  onUndoCompleted,
  sessionId,
  fetchWithAuth,
  className
}) {
  const wsRef = useRef(null);
  const heartbeatIntervalRef = useRef(
    null
  );
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(
    null
  );
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const maxReconnectAttempts = 5;
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [agentStatus, setAgentStatus] = useState("idle");
  const [canUndo, setCanUndo] = useState(false);
  const streamingMessageIdRef = useRef(null);
  const streamingContentRef = useRef("");
  const activeToolCallsRef = useRef(/* @__PURE__ */ new Map());
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  const handleServerMessage = useCallback(
    (msg) => {
      const type = msg.type;
      switch (type) {
        case "agent:history": {
          const historyMessages = msg.messages;
          if (historyMessages && historyMessages.length > 0) {
            setMessages(
              historyMessages.map((m) => ({
                ...m,
                isStreaming: false,
                toolCalls: (m.toolCalls ?? []).map((tc) => ({
                  ...tc,
                  isExecuting: false
                }))
              }))
            );
          } else if (sessionId && fetchWithAuth) {
            void fetchPersistedHistory(sessionId, fetchWithAuth, setMessages);
          }
          break;
        }
        case "agent:chunk": {
          const messageId = msg.messageId;
          const content = msg.content;
          if (streamingMessageIdRef.current !== messageId) {
            streamingMessageIdRef.current = messageId;
            streamingContentRef.current = "";
            activeToolCallsRef.current = /* @__PURE__ */ new Map();
          }
          streamingContentRef.current += content;
          setMessages((prev) => {
            const existing = prev.find((m) => m.id === messageId);
            if (existing) {
              return prev.map(
                (m) => m.id === messageId ? {
                  ...m,
                  content: streamingContentRef.current,
                  isStreaming: true
                } : m
              );
            }
            return [
              ...prev,
              {
                id: messageId,
                role: "assistant",
                content: streamingContentRef.current,
                isStreaming: true,
                toolCalls: [],
                timestamp: Date.now()
              }
            ];
          });
          break;
        }
        case "agent:message": {
          const messageId = msg.messageId;
          const content = msg.content;
          streamingMessageIdRef.current = null;
          streamingContentRef.current = "";
          setMessages((prev) => {
            const existing = prev.find((m) => m.id === messageId);
            if (existing) {
              return prev.map(
                (m) => m.id === messageId ? { ...m, content, isStreaming: false } : m
              );
            }
            return [
              ...prev,
              {
                id: messageId,
                role: "assistant",
                content,
                isStreaming: false,
                toolCalls: [],
                timestamp: Date.now()
              }
            ];
          });
          break;
        }
        case "agent:tool-start": {
          const messageId = msg.messageId;
          const toolCallId = msg.toolCallId;
          const toolName = msg.toolName;
          const toolInput = msg.toolInput;
          const toolCall = {
            id: toolCallId,
            name: toolName,
            input: toolInput,
            isExecuting: true
          };
          activeToolCallsRef.current.set(toolCallId, toolCall);
          setMessages(
            (prev) => prev.map(
              (m) => m.id === messageId ? { ...m, toolCalls: [...m.toolCalls, toolCall] } : m
            )
          );
          break;
        }
        case "agent:tool-result": {
          const toolCallId = msg.toolCallId;
          const success = msg.success;
          const output = msg.output;
          const durationMs = msg.durationMs;
          const existingTc = activeToolCallsRef.current.get(toolCallId);
          if (existingTc) {
            existingTc.success = success;
            existingTc.output = output;
            existingTc.durationMs = durationMs;
            existingTc.isExecuting = false;
          }
          setMessages(
            (prev) => prev.map((m) => {
              const updated = m.toolCalls.map(
                (tc) => tc.id === toolCallId ? { ...tc, success, output, durationMs, isExecuting: false } : tc
              );
              return { ...m, toolCalls: updated };
            })
          );
          break;
        }
        case "agent:status": {
          const status = msg.status;
          setAgentStatus(status);
          break;
        }
        case "agent:error": {
          const errorMessage = msg.message;
          setMessages((prev) => [
            ...prev,
            {
              id: `error_${Date.now()}`,
              role: "assistant",
              content: `Something went wrong: ${errorMessage}`,
              toolCalls: [],
              isStreaming: false,
              timestamp: Date.now()
            }
          ]);
          setAgentStatus("idle");
          break;
        }
        case "agent:undo-available": {
          setCanUndo(msg.canUndo);
          break;
        }
        case "agent:undo-result": {
          const undoMessage = msg.message;
          const undoSuccess = msg.success;
          setMessages((prev) => [
            ...prev,
            {
              id: `undo_${Date.now()}`,
              role: "assistant",
              content: undoSuccess ? `Undone! ${undoMessage}` : `Undo failed: ${undoMessage}`,
              toolCalls: [],
              isStreaming: false,
              timestamp: Date.now()
            }
          ]);
          if (undoSuccess) {
            onUndoCompleted?.();
          }
          break;
        }
      }
    },
    [sessionId, fetchWithAuth, onUndoCompleted]
  );
  useEffect(() => {
    if (!websocketUrl || sessionStatus !== "active") {
      setIsConnected(false);
      setIsConnecting(false);
      reconnectAttemptRef.current = 0;
      return;
    }
    let disposed = false;
    const wsUrl = websocketUrl;
    function connect() {
      if (disposed) return;
      const isReconnect = reconnectAttemptRef.current > 0;
      if (!isReconnect) {
        setIsConnecting(true);
      }
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        if (disposed) {
          ws.close();
          return;
        }
        setIsConnected(true);
        setIsConnecting(false);
        reconnectAttemptRef.current = 0;
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "heartbeat" }));
          }
        }, 3e4);
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleServerMessage(msg);
        } catch {
        }
      };
      ws.onerror = () => {
      };
      ws.onclose = () => {
        if (disposed) return;
        setIsConnected(false);
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        if (reconnectAttemptRef.current < maxReconnectAttempts) {
          reconnectAttemptRef.current += 1;
          const delay = Math.min(
            1e3 * Math.pow(2, reconnectAttemptRef.current - 1),
            3e4
          );
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };
    }
    connect();
    return () => {
      disposed = true;
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, [websocketUrl, sessionStatus, handleServerMessage]);
  const sendMessage = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    const messageId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      {
        id: messageId,
        role: "user",
        content: trimmed,
        toolCalls: [],
        isStreaming: false,
        timestamp: Date.now()
      }
    ]);
    const contentToSend = elementContext ? `[The user is pointing at ${elementContext.description} in the preview.]

${trimmed}` : trimmed;
    wsRef.current.send(
      JSON.stringify({
        type: "agent:send",
        messageId,
        content: contentToSend
      })
    );
    if (elementContext) {
      onElementContextConsumed?.();
    }
    setInputValue("");
    inputRef.current?.focus();
  }, [inputValue, elementContext, onElementContextConsumed]);
  const handleUndo = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "agent:undo" }));
    }
  }, []);
  const handleStop = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "agent:stop" }));
    }
  }, []);
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );
  const isBusy = agentStatus === "thinking" || agentStatus === "tool_executing";
  const canSend = isConnected && !isBusy && inputValue.trim().length > 0;
  return /* @__PURE__ */ jsxs("div", { className: cn(agentChatPanelVariants(), className), children: [
    isBusy && /* @__PURE__ */ jsxs("div", { className: "border-border flex items-center gap-2 border-b px-4 py-2", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-3.5 w-3.5 animate-spin" }),
      /* @__PURE__ */ jsx("span", { className: "text-foreground/60 text-xs", children: agentStatus === "thinking" ? "Agent is thinking..." : "Running tool..." }),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: handleStop,
          className: "text-foreground/40 hover:text-foreground ml-auto text-xs",
          children: /* @__PURE__ */ jsx(Square, { className: "h-3 w-3" })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-4 overflow-y-auto p-4", children: [
      messages.length === 0 && isConnected && /* @__PURE__ */ jsx("div", { className: "flex h-full items-center justify-center", children: /* @__PURE__ */ jsx("p", { className: "text-foreground/40 text-sm", children: "Describe the changes you'd like to make..." }) }),
      messages.map((msg) => /* @__PURE__ */ jsx(
        AgentMessage,
        {
          role: msg.role,
          content: msg.content,
          toolCalls: msg.toolCalls,
          isStreaming: msg.isStreaming,
          timestamp: msg.timestamp
        },
        msg.id
      )),
      /* @__PURE__ */ jsx("div", { ref: messagesEndRef })
    ] }),
    !isBusy && canUndo && isConnected && /* @__PURE__ */ jsx("div", { className: "border-border flex items-center justify-center border-t px-4 py-2", children: /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: handleUndo,
        className: "text-foreground/60 hover:text-foreground hover:bg-surface-2 flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-colors",
        children: [
          /* @__PURE__ */ jsx(Undo2, { className: "h-3.5 w-3.5" }),
          "Undo last change"
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: "border-border border-t p-3", children: [
      elementContext && /* @__PURE__ */ jsx("div", { className: "mb-2 flex items-center gap-2", children: /* @__PURE__ */ jsxs("div", { className: "bg-primary/10 text-primary flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium", children: [
        /* @__PURE__ */ jsxs("span", { className: "max-w-[250px] truncate", children: [
          "Referencing: ",
          elementContext.description
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => onElementContextConsumed?.(),
            className: "text-primary/60 hover:text-primary -mr-1 rounded-full p-0.5",
            children: /* @__PURE__ */ jsx(X, { className: "h-3 w-3" })
          }
        )
      ] }) }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
        /* @__PURE__ */ jsx(
          "textarea",
          {
            ref: inputRef,
            value: inputValue,
            onChange: (e) => setInputValue(e.target.value),
            onKeyDown: handleKeyDown,
            placeholder: !isConnected ? "Connecting..." : isBusy ? "Agent is working..." : "Describe what you want to change...",
            disabled: !isConnected || isBusy,
            rows: 1,
            className: cn(
              "border-border bg-surface-2 text-foreground w-full resize-none rounded-lg border px-3 py-2.5 text-sm",
              "placeholder:text-foreground/40",
              "focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "max-h-32 overflow-y-auto"
            ),
            style: { minHeight: "40px" }
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: sendMessage,
            disabled: !canSend,
            className: cn(
              "bg-primary text-primary-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
              "hover:bg-primary/90 transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-40"
            ),
            children: /* @__PURE__ */ jsx(Send, { className: "h-4 w-4" })
          }
        )
      ] })
    ] }),
    (isConnecting || !isConnected && sessionStatus === "active") && /* @__PURE__ */ jsx("div", { className: "bg-surface-1/90 absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-3", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-8 w-8 animate-spin" }),
      /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: isConnecting ? "Connecting..." : "Waiting for connection..." })
    ] }) }),
    sessionStatus === "provisioning" && /* @__PURE__ */ jsx("div", { className: "bg-surface-1/90 absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-3", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-8 w-8 animate-spin" }),
      /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: "Setting up development environment..." })
    ] }) }),
    sessionStatus === "suspended" && /* @__PURE__ */ jsx("div", { className: "bg-surface-1/90 absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx("div", { className: "flex flex-col items-center gap-3", children: /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: "Session suspended" }) }) })
  ] });
}
var elementSelectorOverlayVariants = cva(
  "absolute inset-0 z-20 cursor-crosshair transition-colors",
  {
    variants: {
      active: {
        true: "bg-primary/5 border-2 border-dashed border-primary",
        false: "pointer-events-none"
      }
    },
    defaultVariants: {
      active: false
    }
  }
);
function formatElementDescription(info) {
  const parts = [];
  parts.push(`the <${info.tagName.toLowerCase()}>`);
  if (info.id) {
    parts.push(`element with id="${info.id}"`);
  } else if (info.className) {
    const classes = info.className.split(" ").slice(0, 3).join(" ");
    parts.push(`element with class="${classes}"`);
  } else {
    parts.push("element");
  }
  if (info.selector) {
    parts.push(`(CSS selector: ${info.selector})`);
  }
  if (info.textContent) {
    const truncated = info.textContent.length > 50 ? info.textContent.slice(0, 50) + "..." : info.textContent;
    parts.push(`containing "${truncated}"`);
  }
  return parts.join(" ");
}
function ElementSelectorOverlay({
  isActive,
  iframeRef,
  onElementSelected,
  onDisable,
  className
}) {
  const overlayRef = useRef(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  useEffect(() => {
    if (!isActive) return;
    function handleMessage(event) {
      if (!iframeRef.current) return;
      const data = event.data;
      if (typeof data !== "object" || data === null) return;
      if (data.type === "element-selector:result") {
        setIsWaitingForResponse(false);
        const info = {
          tagName: data.tagName || "unknown",
          selector: data.selector || "",
          textContent: data.textContent || "",
          className: data.className || "",
          id: data.id || "",
          x: data.x || 0,
          y: data.y || 0
        };
        const description = formatElementDescription(info);
        onElementSelected(info, description);
        onDisable?.();
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isActive, iframeRef, onElementSelected, onDisable]);
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!isActive || !iframe?.contentWindow) return;
    iframe.contentWindow.postMessage({ type: "element-selector:enable" }, "*");
    return () => {
      iframe.contentWindow?.postMessage(
        { type: "element-selector:disable" },
        "*"
      );
    };
  }, [isActive, iframeRef]);
  const handleClick = useCallback(
    (event) => {
      if (!isActive || !iframeRef.current?.contentWindow) return;
      event.preventDefault();
      event.stopPropagation();
      const iframe = iframeRef.current;
      const iframeRect = iframe.getBoundingClientRect();
      const x = event.clientX - iframeRect.left;
      const y = event.clientY - iframeRect.top;
      setIsWaitingForResponse(true);
      iframe.contentWindow?.postMessage(
        {
          type: "element-selector:click",
          x,
          y
        },
        "*"
      );
      setTimeout(() => {
        setIsWaitingForResponse((waiting) => {
          if (waiting) {
            const fallbackInfo = {
              tagName: "element",
              selector: "",
              textContent: "",
              className: "",
              id: "",
              x,
              y
            };
            const description = `the element at position (${Math.round(x)}, ${Math.round(y)}) in the preview`;
            onElementSelected(fallbackInfo, description);
            onDisable?.();
          }
          return false;
        });
      }, 2e3);
    },
    [isActive, iframeRef, onElementSelected, onDisable]
  );
  const handleMouseMove = useCallback(
    (event) => {
      if (!isActive) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setHoverPosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      });
    },
    [isActive]
  );
  const handleMouseLeave = useCallback(() => {
    setHoverPosition(null);
  }, []);
  useEffect(() => {
    if (!isActive) return;
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onDisable?.();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isActive, onDisable]);
  if (!isActive) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: overlayRef,
      className: cn(
        elementSelectorOverlayVariants({ active: isActive }),
        className
      ),
      onClick: handleClick,
      onMouseMove: handleMouseMove,
      onMouseLeave: handleMouseLeave,
      children: [
        /* @__PURE__ */ jsx("div", { className: "bg-primary text-primary-foreground absolute top-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-2 text-sm font-medium shadow-lg", children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(MousePointerClick, { className: "h-4 w-4" }),
          "Click an element to select it \u2022 Press Esc to cancel"
        ] }) }),
        hoverPosition && !isWaitingForResponse && /* @__PURE__ */ jsx(
          "div",
          {
            className: "pointer-events-none absolute",
            style: {
              left: hoverPosition.x - 10,
              top: hoverPosition.y - 10
            },
            children: /* @__PURE__ */ jsx("div", { className: "bg-primary h-5 w-5 rounded-full opacity-50" })
          }
        ),
        isWaitingForResponse && /* @__PURE__ */ jsx("div", { className: "bg-surface-1/90 absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "bg-surface-2 flex items-center gap-2 rounded-lg px-4 py-2 shadow-lg", children: [
          /* @__PURE__ */ jsx("div", { className: "bg-primary h-2 w-2 animate-pulse rounded-full" }),
          /* @__PURE__ */ jsx("span", { className: "text-foreground text-sm", children: "Analyzing element..." })
        ] }) })
      ]
    }
  );
}
var previewToolbarVariants = cva(
  "flex items-center justify-between gap-4 border-b border-border bg-surface-1 px-4 py-2"
);
var viewportConfig = {
  desktop: { icon: Monitor, label: "Desktop", width: "100%" },
  tablet: { icon: Tablet, label: "Tablet", width: "768px" },
  mobile: { icon: Smartphone, label: "Mobile", width: "375px" }
};
function PreviewToolbar({
  onRefresh,
  viewport,
  onViewportChange,
  isSelectorActive = false,
  onSelectorToggle,
  className
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn(previewToolbarVariants(), className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-1 items-center gap-2", children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "ghost",
          size: "sm",
          onClick: onRefresh,
          className: "flex-shrink-0",
          title: "Refresh preview",
          children: /* @__PURE__ */ jsx(RefreshCw, { className: "h-4 w-4" })
        }
      ),
      onSelectorToggle && /* @__PURE__ */ jsx(
        Button,
        {
          variant: isSelectorActive ? "default" : "ghost",
          size: "sm",
          onClick: onSelectorToggle,
          className: cn(
            "flex-shrink-0",
            isSelectorActive && "bg-primary text-primary-foreground"
          ),
          title: "Select element (click to inspect)",
          children: /* @__PURE__ */ jsx(MousePointerClick, { className: "h-4 w-4" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-shrink-0 items-center gap-1", children: Object.keys(viewportConfig).map((key) => {
      const config = viewportConfig[key];
      const Icon3 = config.icon;
      const isActive = viewport === key;
      return /* @__PURE__ */ jsxs(
        Button,
        {
          variant: isActive ? "default" : "ghost",
          size: "sm",
          onClick: () => onViewportChange(key),
          className: cn(
            "flex items-center gap-2",
            !isActive && "text-foreground/60"
          ),
          title: config.label,
          children: [
            /* @__PURE__ */ jsx(Icon3, { className: "h-4 w-4" }),
            /* @__PURE__ */ jsx("span", { className: "hidden sm:inline", children: config.label })
          ]
        },
        key
      );
    }) })
  ] });
}
var previewPanelVariants = cva(
  "flex h-full w-full flex-col overflow-hidden bg-surface-2"
);
var viewportWidths = {
  desktop: "100%",
  tablet: "768px",
  mobile: "375px"
};
function PreviewPanel({
  previewUrl,
  sessionStatus,
  onElementSelected,
  className
}) {
  const [viewport, setViewport] = useState(
    "desktop"
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSelectorActive, setIsSelectorActive] = useState(false);
  const iframeRef = useRef(null);
  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setIsLoading(true);
  };
  const handleLoad = () => {
    setIsLoading(false);
  };
  const handleSelectorToggle = () => {
    setIsSelectorActive((prev) => !prev);
  };
  const handleElementSelected = (info, description) => {
    onElementSelected?.(info, description);
  };
  const handleSelectorDisable = () => {
    setIsSelectorActive(false);
  };
  const isReady = previewUrl && sessionStatus === "active";
  return /* @__PURE__ */ jsxs("div", { className: cn(previewPanelVariants(), className), children: [
    isReady && /* @__PURE__ */ jsx(
      PreviewToolbar,
      {
        onRefresh: handleRefresh,
        viewport,
        onViewportChange: setViewport,
        isSelectorActive,
        onSelectorToggle: onElementSelected ? handleSelectorToggle : void 0
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "bg-surface-2 relative flex flex-1 items-center justify-center overflow-auto", children: !isReady ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-3", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-8 w-8 animate-spin" }),
      /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: sessionStatus === "provisioning" ? "Setting up preview..." : sessionStatus === "suspended" ? "Session suspended" : "Waiting for preview..." })
    ] }) : /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "relative h-full transition-all duration-300",
          viewport === "desktop" && "w-full",
          viewport !== "desktop" && "border-border border-x shadow-xl"
        ),
        style: {
          width: viewportWidths[viewport],
          maxWidth: "100%"
        },
        children: [
          isLoading && /* @__PURE__ */ jsx("div", { className: "bg-surface-2/90 absolute inset-0 z-10 flex items-center justify-center", children: /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-8 w-8 animate-spin" }) }),
          /* @__PURE__ */ jsx(
            ElementSelectorOverlay,
            {
              isActive: isSelectorActive,
              iframeRef,
              onElementSelected: handleElementSelected,
              onDisable: handleSelectorDisable
            }
          ),
          /* @__PURE__ */ jsx(
            "iframe",
            {
              ref: iframeRef,
              src: previewUrl,
              className: "h-full w-full border-0",
              title: "Preview",
              sandbox: "allow-same-origin allow-scripts allow-forms allow-popups allow-modals",
              onLoad: handleLoad
            },
            refreshKey
          )
        ]
      }
    ) })
  ] });
}
var provisioningViewVariants = cva(
  "flex h-full w-full items-center justify-center bg-surface-1"
);
var statusConfig4 = {
  pending: {
    icon: Circle,
    color: "text-foreground/60",
    bgColor: "bg-surface-2"
  },
  active: {
    icon: Loader2,
    color: "text-primary",
    bgColor: "bg-primary/10"
  },
  completed: {
    icon: CheckCircle2,
    color: "text-success",
    bgColor: "bg-success/10"
  },
  failed: {
    icon: XCircle,
    color: "text-error",
    bgColor: "bg-error/10"
  }
};
function ProvisioningView({ steps, className }) {
  return /* @__PURE__ */ jsx("div", { className: cn(provisioningViewVariants(), className), children: /* @__PURE__ */ jsxs("div", { className: "w-full max-w-2xl space-y-4 px-6", children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-8 text-center", children: [
      /* @__PURE__ */ jsx("h2", { className: "text-foreground text-2xl font-semibold", children: "Setting Up Development Environment" }),
      /* @__PURE__ */ jsx("p", { className: "text-foreground/60 mt-2 text-base", children: "This may take a few minutes..." })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "space-y-3", "aria-live": "polite", children: steps.map((step, index) => {
      const config = statusConfig4[step.status];
      const Icon3 = config.icon;
      const isActive = step.status === "active";
      const isCompleted = step.status === "completed";
      const isFailed = step.status === "failed";
      return /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "border-border flex items-start gap-4 rounded-lg border p-4 transition-all",
            config.bgColor
          ),
          children: [
            /* @__PURE__ */ jsx("div", { className: "flex-shrink-0 pt-0.5", children: /* @__PURE__ */ jsx(
              Icon3,
              {
                className: cn(
                  "h-6 w-6",
                  config.color,
                  isActive && "animate-spin"
                )
              }
            ) }),
            /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-1", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs("span", { className: "text-foreground text-base font-medium", children: [
                  index + 1,
                  ". ",
                  step.label
                ] }),
                isActive && /* @__PURE__ */ jsx("span", { className: "text-primary text-sm", children: "In progress" }),
                isCompleted && /* @__PURE__ */ jsx("span", { className: "text-success text-sm", children: "Complete" }),
                isFailed && /* @__PURE__ */ jsx("span", { className: "text-error text-sm", children: "Failed" })
              ] }),
              step.message && /* @__PURE__ */ jsx(
                "p",
                {
                  className: cn(
                    "text-base",
                    isActive ? "text-foreground" : "text-foreground/60"
                  ),
                  children: step.message
                }
              )
            ] })
          ]
        },
        step.id
      );
    }) }),
    /* @__PURE__ */ jsx("div", { className: "mt-8", children: /* @__PURE__ */ jsx("div", { className: "bg-surface-2 h-2 w-full overflow-hidden rounded-full", children: /* @__PURE__ */ jsx(
      "div",
      {
        className: "bg-primary h-full transition-all duration-300",
        style: {
          width: `${steps.filter((s) => s.status === "completed").length / steps.length * 100}%`
        }
      }
    ) }) })
  ] }) });
}
var splitPaneVariants = cva("flex h-full w-full overflow-hidden");
function SplitPane({
  left,
  right,
  defaultLeftWidth = 40,
  minLeftWidth = 300,
  minRightWidth = 300,
  className
}) {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;
      let newPercentage = mouseX / containerWidth * 100;
      const minLeftPercentage = minLeftWidth / containerWidth * 100;
      const minRightPercentage = minRightWidth / containerWidth * 100;
      if (newPercentage < minLeftPercentage) {
        newPercentage = minLeftPercentage;
      } else if (newPercentage > 100 - minRightPercentage) {
        newPercentage = 100 - minRightPercentage;
      }
      setLeftWidth(newPercentage);
    },
    [isDragging, minLeftWidth, minRightWidth]
  );
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn(splitPaneVariants(), className), children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        style: { width: `${leftWidth}%` },
        className: "relative h-full overflow-hidden",
        children: left
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        onMouseDown: handleMouseDown,
        className: cn(
          "group bg-border hover:bg-primary/20 relative flex w-1 cursor-col-resize items-center justify-center transition-colors",
          isDragging && "bg-primary/20"
        ),
        children: [
          /* @__PURE__ */ jsx("div", { className: "absolute inset-y-0 -right-1 -left-1" }),
          /* @__PURE__ */ jsx(
            GripVertical,
            {
              className: cn(
                "text-foreground/60 h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100",
                isDragging && "opacity-100"
              )
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        style: { width: `${100 - leftWidth}%` },
        className: "relative h-full overflow-hidden",
        children: right
      }
    )
  ] });
}
var terminalPanelVariants = cva(
  "relative h-full w-full overflow-hidden bg-surface-2"
);
function TerminalPanel({
  websocketUrl,
  sessionStatus,
  className
}) {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const wsRef = useRef(null);
  const fitAddonRef = useRef(null);
  const heartbeatIntervalRef = useRef(
    null
  );
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef(
    null
  );
  const initIdRef = useRef(0);
  const maxReconnectAttempts = 5;
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isTerminalReady, setIsTerminalReady] = useState(false);
  useEffect(() => {
    let cleanup;
    initIdRef.current += 1;
    const myInitId = initIdRef.current;
    async function init() {
      if (!terminalRef.current) return;
      try {
        const [{ Terminal: Terminal2 }, { FitAddon }, { WebLinksAddon }] = await Promise.all([
          import('@xterm/xterm'),
          import('@xterm/addon-fit'),
          import('@xterm/addon-web-links')
        ]);
        if (myInitId !== initIdRef.current || !terminalRef.current) return;
        if (xtermRef.current) {
          xtermRef.current.dispose();
          xtermRef.current = null;
        }
        while (terminalRef.current.firstChild) {
          terminalRef.current.removeChild(terminalRef.current.firstChild);
        }
        const terminal = new Terminal2({
          cursorBlink: true,
          fontSize: 14,
          fontFamily: 'Menlo, Monaco, "Courier New", monospace',
          theme: {
            background: "#030c30",
            foreground: "#e5e5e5",
            cursor: "#e5e5e5",
            cursorAccent: "#030c30",
            selectionBackground: "#232eff",
            black: "#030c30",
            red: "#dc2626",
            green: "#16a34a",
            yellow: "#ca8a04",
            blue: "#232eff",
            magenta: "#a855f7",
            cyan: "#06b6d4",
            white: "#e5e5e5",
            brightBlack: "#525252",
            brightRed: "#f26649",
            brightGreen: "#4ade80",
            brightYellow: "#f89d3e",
            brightBlue: "#c7eafb",
            brightMagenta: "#c084fc",
            brightCyan: "#22d3ee",
            brightWhite: "#ffffff"
          }
        });
        const fitAddon = new FitAddon();
        const webLinksAddon = new WebLinksAddon();
        terminal.loadAddon(fitAddon);
        terminal.loadAddon(webLinksAddon);
        terminal.open(terminalRef.current);
        fitAddon.fit();
        xtermRef.current = terminal;
        fitAddonRef.current = fitAddon;
        setIsTerminalReady(true);
        const resizeObserver = new ResizeObserver(() => {
          fitAddon.fit();
        });
        resizeObserver.observe(terminalRef.current);
        cleanup = () => {
          resizeObserver.disconnect();
          terminal.dispose();
          xtermRef.current = null;
          fitAddonRef.current = null;
          setIsTerminalReady(false);
        };
      } catch (error) {
        console.error("Failed to initialize terminal:", error);
      }
    }
    void init();
    return () => {
      cleanup?.();
    };
  }, []);
  useEffect(() => {
    if (!websocketUrl || !xtermRef.current || sessionStatus !== "active") {
      setIsConnected(false);
      setIsConnecting(false);
      setIsReconnecting(false);
      reconnectAttemptRef.current = 0;
      return;
    }
    let disposed = false;
    const terminal = xtermRef.current;
    const wsUrl = websocketUrl;
    let disposable = null;
    let resizeDisposable = null;
    function connect() {
      if (disposed) return;
      const isReconnect = reconnectAttemptRef.current > 0;
      if (isReconnect) {
        setIsReconnecting(true);
        terminal.write(
          `\r
\x1B[33mReconnecting (attempt ${reconnectAttemptRef.current}/${maxReconnectAttempts})...\x1B[0m\r
`
        );
      } else {
        setIsConnecting(true);
      }
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        if (disposed) {
          ws.close();
          return;
        }
        setIsConnected(true);
        setIsConnecting(false);
        setIsReconnecting(false);
        reconnectAttemptRef.current = 0;
        if (isReconnect) {
          terminal.write(
            "\r\n\x1B[32mReconnected to development environment\x1B[0m\r\n"
          );
        } else {
          terminal.write(
            "\r\n\x1B[32mConnected to development environment\x1B[0m\r\n"
          );
        }
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "heartbeat" }));
          }
        }, 3e4);
      };
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "pty:output" && message.data) {
            terminal.write(message.data);
          } else if (message.type === "error") {
            terminal.write(`\r
\x1B[31mError: ${message.message}\x1B[0m\r
`);
          }
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
        }
      };
      ws.onerror = () => {
      };
      ws.onclose = () => {
        if (disposed) return;
        setIsConnected(false);
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        if (reconnectAttemptRef.current < maxReconnectAttempts) {
          reconnectAttemptRef.current += 1;
          const delay = Math.min(
            1e3 * Math.pow(2, reconnectAttemptRef.current - 1),
            3e4
          );
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        } else {
          setIsReconnecting(false);
          setIsConnecting(false);
          terminal.write(
            "\r\n\x1B[31mConnection lost. Please refresh the page.\x1B[0m\r\n"
          );
        }
      };
      disposable?.dispose();
      disposable = terminal.onData((data) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              type: "pty:input",
              data
            })
          );
        }
      });
      resizeDisposable?.dispose();
      resizeDisposable = terminal.onResize(
        ({ cols, rows }) => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                type: "pty:resize",
                cols,
                rows
              })
            );
          }
        }
      );
    }
    connect();
    return () => {
      disposed = true;
      disposable?.dispose();
      resizeDisposable?.dispose();
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, [websocketUrl, sessionStatus, isTerminalReady]);
  return /* @__PURE__ */ jsxs("div", { className: cn(terminalPanelVariants(), className), children: [
    /* @__PURE__ */ jsx("div", { ref: terminalRef, className: "h-full w-full p-2" }),
    (isConnecting || isReconnecting || !isConnected && sessionStatus === "active") && /* @__PURE__ */ jsx("div", { className: "bg-surface-2/90 absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-3", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-8 w-8 animate-spin" }),
      /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: isReconnecting ? `Reconnecting (attempt ${reconnectAttemptRef.current}/${maxReconnectAttempts})...` : isConnecting ? "Connecting..." : "Waiting for connection..." })
    ] }) }),
    sessionStatus === "provisioning" && /* @__PURE__ */ jsx("div", { className: "bg-surface-2/90 absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-3", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "text-primary h-8 w-8 animate-spin" }),
      /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: "Setting up development environment..." })
    ] }) }),
    sessionStatus === "suspended" && /* @__PURE__ */ jsx("div", { className: "bg-surface-2/90 absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx("div", { className: "flex flex-col items-center gap-3", children: /* @__PURE__ */ jsx("p", { className: "text-foreground/60 text-base", children: "Session suspended" }) }) })
  ] });
}
var topBarVariants = cva(
  "flex items-center justify-between gap-4 border-b border-border bg-surface-1 px-6 py-3"
);
var statusConfig5 = {
  provisioning: {
    label: "Provisioning",
    icon: Loader2,
    color: "text-info"
  },
  active: {
    label: "Active",
    icon: CheckCircle2,
    color: "text-success"
  },
  suspended: {
    label: "Suspended",
    icon: Pause,
    color: "text-warning"
  },
  finalizing: {
    label: "Finalizing",
    icon: Loader2,
    color: "text-info"
  },
  destroyed: {
    label: "Destroyed",
    icon: XCircle,
    color: "text-error"
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    color: "text-error"
  }
};
function formatDuration(startedAt) {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const diffMs = now - start;
  const hours = Math.floor(diffMs / (1e3 * 60 * 60));
  const minutes = Math.floor(diffMs % (1e3 * 60 * 60) / (1e3 * 60));
  const seconds = Math.floor(diffMs % (1e3 * 60) / 1e3);
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}
function TopBar({
  requestTitle,
  sessionStatus,
  onPause,
  onFinalize,
  onBack,
  onReset,
  isPausing = false,
  isFinalizing = false,
  isResetting = false,
  sessionStartedAt,
  className
}) {
  const [duration, setDuration] = useState("0s");
  const config = statusConfig5[sessionStatus];
  const StatusIcon = config.icon;
  useEffect(() => {
    if (!sessionStartedAt || sessionStatus === "destroyed") return;
    const updateDuration = () => {
      setDuration(formatDuration(sessionStartedAt));
    };
    updateDuration();
    const interval = setInterval(updateDuration, 1e3);
    return () => clearInterval(interval);
  }, [sessionStartedAt, sessionStatus]);
  return /* @__PURE__ */ jsxs("div", { className: cn(topBarVariants(), className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-4", children: [
      /* @__PURE__ */ jsxs(
        Button,
        {
          variant: "ghost",
          size: "sm",
          onClick: onBack,
          className: "flex items-center gap-2",
          children: [
            /* @__PURE__ */ jsx(ArrowLeft, { className: "h-4 w-4" }),
            "Back"
          ]
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "bg-border h-6 w-px" }),
      /* @__PURE__ */ jsx("h1", { className: "text-foreground text-lg font-semibold", children: requestTitle })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx(
        StatusIcon,
        {
          className: cn(
            "h-5 w-5",
            config.color,
            (sessionStatus === "provisioning" || sessionStatus === "finalizing") && "animate-spin"
          )
        }
      ),
      /* @__PURE__ */ jsx("span", { className: "text-foreground text-base font-medium", children: config.label }),
      sessionStartedAt && sessionStatus !== "destroyed" && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "bg-border h-4 w-px" }),
        /* @__PURE__ */ jsx(Clock, { className: "text-foreground/60 h-4 w-4" }),
        /* @__PURE__ */ jsx("span", { className: "text-foreground/60 text-base", children: duration })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      sessionStatus === "active" && /* @__PURE__ */ jsxs(Fragment, { children: [
        onReset && /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "ghost",
            size: "sm",
            onClick: onReset,
            disabled: isResetting || isPausing || isFinalizing,
            className: "text-foreground/60 hover:text-destructive flex items-center gap-2",
            title: "Reset session and start over",
            children: [
              isResetting ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx(RotateCcw, { className: "h-4 w-4" }),
              "Reset"
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: onPause,
            disabled: isPausing || isResetting,
            className: "flex items-center gap-2",
            children: [
              isPausing ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx(Pause, { className: "h-4 w-4" }),
              "Pause Session"
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "default",
            size: "sm",
            onClick: onFinalize,
            disabled: isFinalizing || isResetting,
            className: "flex items-center gap-2",
            children: [
              isFinalizing ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx(Save, { className: "h-4 w-4" }),
              "Finalize"
            ]
          }
        )
      ] }),
      sessionStatus === "suspended" && /* @__PURE__ */ jsxs(Fragment, { children: [
        onReset && /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "ghost",
            size: "sm",
            onClick: onReset,
            disabled: isResetting || isPausing,
            className: "text-foreground/60 hover:text-destructive flex items-center gap-2",
            title: "Reset session and start over",
            children: [
              isResetting ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx(RotateCcw, { className: "h-4 w-4" }),
              "Reset"
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Button,
          {
            variant: "default",
            size: "sm",
            onClick: onPause,
            disabled: isPausing || isResetting,
            className: "flex items-center gap-2",
            children: [
              isPausing ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin" }) : /* @__PURE__ */ jsx(CheckCircle2, { className: "h-4 w-4" }),
              "Resume Session"
            ]
          }
        )
      ] })
    ] })
  ] });
}
var AuthTemplate = React17.forwardRef(
  ({
    className,
    title,
    subtitle,
    logoSrc = "/cortex-logo.webp",
    centered,
    children,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      Center,
      {
        ref,
        className: cn("bg-background min-h-screen", className),
        ...props,
        children: /* @__PURE__ */ jsxs(
          Stack,
          {
            gap: "xl",
            className: cn("w-full max-w-md p-8", centered && "text-center"),
            children: [
              /* @__PURE__ */ jsxs(Stack, { align: "center", gap: "lg", children: [
                /* @__PURE__ */ jsx(Image3, { src: logoSrc, alt: "Cortex", className: "h-12 w-auto" }),
                /* @__PURE__ */ jsx(Heading, { level: 2, className: "text-3xl font-bold", children: title }),
                subtitle && /* @__PURE__ */ jsx(Text, { color: "secondary", className: "-mt-4", children: subtitle })
              ] }),
              children
            ]
          }
        )
      }
    );
  }
);
AuthTemplate.displayName = "AuthTemplate";
var ChatTemplate = React17.forwardRef(
  ({
    className,
    header,
    children,
    sidebar,
    showSidebar = true,
    sidebarWidth = "280px",
    mobileSidebarOpen = false,
    onMobileSidebarClose,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("bg-surface-2 flex h-full", className),
        ...props,
        children: [
          sidebar && mobileSidebarOpen && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "fixed inset-0 z-40 bg-black/50 lg:hidden",
                onClick: onMobileSidebarClose,
                "aria-hidden": "true"
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "border-border bg-surface-1 fixed inset-y-0 left-0 z-50 border-r lg:hidden",
                style: { width: sidebarWidth },
                children: sidebar
              }
            )
          ] }),
          sidebar && showSidebar && /* @__PURE__ */ jsx(
            "aside",
            {
              className: "border-border bg-surface-1 hidden shrink-0 overflow-y-auto border-r lg:block",
              style: { width: sidebarWidth },
              tabIndex: 0,
              children: sidebar
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-1 flex-col", children: [
            header && /* @__PURE__ */ jsx("div", { className: "border-border bg-surface-1 shrink-0 border-b", children: header }),
            /* @__PURE__ */ jsx("main", { className: "flex-1 overflow-hidden", children })
          ] })
        ]
      }
    );
  }
);
ChatTemplate.displayName = "ChatTemplate";
var DashboardTemplate = React17.forwardRef(
  ({
    className,
    title,
    subtitle,
    actions,
    breadcrumb,
    children,
    contained = true,
    maxWidth = "1280px",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("min-h-full px-4 py-6 lg:px-8", className),
        ...props,
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(contained && "mx-auto w-full"),
            style: contained ? { maxWidth } : void 0,
            children: [
              breadcrumb && /* @__PURE__ */ jsx("div", { className: "mb-4", children: breadcrumb }),
              /* @__PURE__ */ jsxs("div", { className: "mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between", children: [
                /* @__PURE__ */ jsxs("div", { children: [
                  /* @__PURE__ */ jsx("h1", { className: "text-foreground text-2xl font-bold", children: title }),
                  subtitle && /* @__PURE__ */ jsx("p", { className: "text-foreground/70 mt-1 text-base", children: subtitle })
                ] }),
                actions && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-2", children: actions })
              ] }),
              /* @__PURE__ */ jsx("div", { children })
            ]
          }
        )
      }
    );
  }
);
DashboardTemplate.displayName = "DashboardTemplate";
var ProjectTemplate = React17.forwardRef(
  ({
    className,
    header,
    tabs,
    activeTab,
    onTabChange,
    children,
    aside,
    asidePosition = "right",
    asideWidth = "320px",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs("div", { ref, className: cn("min-h-full", className), ...props, children: [
      /* @__PURE__ */ jsxs("div", { className: "border-border bg-surface-1 border-b", children: [
        /* @__PURE__ */ jsx("div", { className: "px-4 py-6 lg:px-8", children: header }),
        tabs && tabs.length > 0 && /* @__PURE__ */ jsx("div", { className: "px-4 lg:px-8", children: /* @__PURE__ */ jsx("nav", { className: "-mb-px flex gap-6", role: "tablist", children: tabs.map((tab) => /* @__PURE__ */ jsxs(
          "button",
          {
            role: "tab",
            "aria-selected": activeTab === tab.id,
            onClick: () => onTabChange?.(tab.id),
            className: cn(
              "border-b-2 pb-4 text-base font-medium transition-colors duration-150",
              "focus-visible:ring-ring focus-visible:ring-offset-background rounded-t focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              activeTab === tab.id ? "border-primary text-primary" : "text-foreground/60 hover:border-border hover:text-foreground border-transparent"
            ),
            children: [
              tab.label,
              tab.badge !== void 0 && tab.badge > 0 && /* @__PURE__ */ jsx("span", { className: "bg-surface-3 text-foreground/70 ml-2 rounded-full px-2 py-0.5 text-base font-medium", children: tab.badge })
            ]
          },
          tab.id
        )) }) })
      ] }),
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn("flex", asidePosition === "left" && "flex-row-reverse"),
          children: [
            /* @__PURE__ */ jsx("main", { className: "flex-1 overflow-y-auto p-4 lg:p-8", tabIndex: 0, children }),
            aside && /* @__PURE__ */ jsx(
              "aside",
              {
                className: cn(
                  "border-border bg-surface-1 hidden shrink-0 overflow-y-auto p-4 lg:block lg:p-6",
                  asidePosition === "right" ? "border-l" : "border-r"
                ),
                style: { width: asideWidth },
                tabIndex: 0,
                children: aside
              }
            )
          ]
        }
      )
    ] });
  }
);
ProjectTemplate.displayName = "ProjectTemplate";
var RequestDetailTemplate = React17.forwardRef(
  ({
    className,
    header,
    breadcrumb,
    children,
    sidebar,
    bottomPanel,
    bottomPanelExpanded = false,
    bottomPanelHeight = "300px",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("flex min-h-full flex-col", className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "border-border bg-surface-1 shrink-0 border-b", children: [
            breadcrumb && /* @__PURE__ */ jsx("div", { className: "px-4 pt-4 lg:px-8", children: breadcrumb }),
            /* @__PURE__ */ jsx("div", { className: "px-4 py-4 lg:px-8", children: header })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-1 overflow-hidden", children: [
            /* @__PURE__ */ jsx("main", { className: "flex-1 overflow-y-auto p-4 lg:p-8", tabIndex: 0, children }),
            sidebar && /* @__PURE__ */ jsx(
              "aside",
              {
                className: "border-border bg-surface-1 hidden w-80 shrink-0 overflow-y-auto border-l p-4 lg:block lg:p-6",
                tabIndex: 0,
                children: sidebar
              }
            )
          ] }),
          bottomPanel && /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "border-border bg-surface-1 shrink-0 overflow-hidden border-t transition-[height] duration-300",
                !bottomPanelExpanded && "h-0"
              ),
              style: { height: bottomPanelExpanded ? bottomPanelHeight : 0 },
              children: bottomPanel
            }
          )
        ]
      }
    );
  }
);
RequestDetailTemplate.displayName = "RequestDetailTemplate";
var SettingsTemplate = React17.forwardRef(
  ({
    className,
    title,
    description,
    sections,
    activeSection,
    onSectionChange,
    children,
    actions,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("bg-surface-2 min-h-full", className),
        ...props,
        children: [
          /* @__PURE__ */ jsx("div", { className: "border-border bg-surface-1 border-b px-4 py-6 lg:px-8", children: /* @__PURE__ */ jsxs("div", { className: "mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between", children: [
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("h1", { className: "text-foreground text-2xl font-bold", children: title }),
              description && /* @__PURE__ */ jsx("p", { className: "text-foreground/70 mt-1 text-base", children: description })
            ] }),
            actions && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-2", children: actions })
          ] }) }),
          /* @__PURE__ */ jsx("div", { className: "mx-auto max-w-5xl px-4 py-6 lg:px-8", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-8 lg:flex-row", children: [
            sections && sections.length > 0 && /* @__PURE__ */ jsx(
              "nav",
              {
                className: "shrink-0 lg:w-64",
                "aria-label": "Settings navigation",
                children: /* @__PURE__ */ jsx("ul", { className: "space-y-1", children: sections.map((section) => {
                  const isActive = activeSection === section.id;
                  return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
                    "button",
                    {
                      onClick: () => onSectionChange?.(section.id),
                      className: cn(
                        "flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-left text-base font-medium transition-colors duration-150",
                        "focus-visible:ring-ring focus-visible:ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                        isActive ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-surface-3 hover:text-foreground"
                      ),
                      "aria-current": isActive ? "page" : void 0,
                      children: [
                        section.icon && /* @__PURE__ */ jsx(section.icon, { className: "h-5 w-5 shrink-0" }),
                        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
                          /* @__PURE__ */ jsx("span", { className: "block truncate", children: section.label }),
                          section.description && /* @__PURE__ */ jsx("span", { className: "text-foreground/60 block truncate text-sm", children: section.description })
                        ] })
                      ]
                    }
                  ) }, section.id);
                }) })
              }
            ),
            /* @__PURE__ */ jsx("main", { className: "min-w-0 flex-1", children: /* @__PURE__ */ jsx("div", { className: "border-border bg-surface-1 rounded-lg border p-6", children }) })
          ] }) })
        ]
      }
    );
  }
);
SettingsTemplate.displayName = "SettingsTemplate";
var WizardTemplate = React17.forwardRef(
  ({
    className,
    title,
    description,
    stepIndicator,
    children,
    showBack = true,
    backLabel = "Back",
    onBack,
    backDisabled = false,
    nextLabel = "Next",
    onNext,
    nextDisabled = false,
    nextLoading = false,
    showCancel = true,
    cancelLabel = "Cancel",
    onCancel,
    additionalActions,
    loading = false,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn("bg-surface-2 flex min-h-full flex-col", className),
        ...props,
        children: [
          /* @__PURE__ */ jsx("div", { className: "border-border bg-surface-1 shrink-0 border-b px-4 py-6 lg:px-8", children: /* @__PURE__ */ jsxs("div", { className: "mx-auto max-w-3xl", children: [
            /* @__PURE__ */ jsx("h1", { className: "text-foreground text-2xl font-bold", children: title }),
            description && /* @__PURE__ */ jsx("p", { className: "text-foreground/70 mt-1 text-base", children: description })
          ] }) }),
          stepIndicator && /* @__PURE__ */ jsx("div", { className: "border-border bg-surface-1 shrink-0 border-b px-4 py-4 lg:px-8", children: /* @__PURE__ */ jsx("div", { className: "mx-auto max-w-3xl", children: stepIndicator }) }),
          /* @__PURE__ */ jsx("main", { className: "flex-1 overflow-y-auto px-4 py-8 lg:px-8", tabIndex: 0, children: /* @__PURE__ */ jsx("div", { className: "mx-auto max-w-3xl", children }) }),
          /* @__PURE__ */ jsx("div", { className: "border-border bg-surface-1 shrink-0 border-t px-4 py-4 lg:px-8", children: /* @__PURE__ */ jsxs("div", { className: "mx-auto flex max-w-3xl items-center justify-between", children: [
            /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: showCancel && onCancel && /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: onCancel, disabled: loading, children: cancelLabel }) }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              additionalActions,
              showBack && onBack && /* @__PURE__ */ jsx(
                Button,
                {
                  variant: "outline",
                  onClick: onBack,
                  disabled: backDisabled || loading,
                  children: backLabel
                }
              ),
              onNext && /* @__PURE__ */ jsx(
                Button,
                {
                  onClick: onNext,
                  disabled: nextDisabled || loading,
                  loading: nextLoading,
                  children: nextLabel
                }
              )
            ] })
          ] }) })
        ]
      }
    );
  }
);
WizardTemplate.displayName = "WizardTemplate";

export { ActivityFeed, ActivityItem, AgentChatPanel, AgentMessage, Alert, AlertDescription, AlertTitle, AuthTemplate, Avatar, AvatarFallback, AvatarImage, AvatarRoot, Badge, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Center, ChatBubble, ChatPanel, ChatTemplate, Checkbox, Container, DashboardTemplate, DataTable, Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, Divider, DividerWithText, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, EmptyState, Form, FormField, GitHubIcon, GoogleIcon, Grid, GridItem, Heading, Icon2 as Icon, IconButton, Image3 as Image, ImageUpload, Inline, Input, Label2 as Label, List, ListItem, LoginForm, MetadataField, MicrosoftIcon, NavItem, NotificationTray, OrgLogo, PageHeader, PipelineBar, PreviewPanel, PreviewToolbar, Progress, ProjectCard, ProjectTemplate, ProvisioningView, RadioGroup2 as RadioGroup, RadioGroupItem, RequestCard, RequestDetailTemplate, ReviewPanel, SearchField, Section, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator, SettingsTemplate, Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, Skeleton, SocialLoginButton, Spinner, SplitPane, Stack, StatCard, StepIndicator, Surface, Switch, Tabs, TabsContent, TabsList, TabsTrigger, TerminalPanel, Text, TextArea, Toaster, ToolCallCard, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TopBar, UserMenu, WizardStep, WizardTemplate, activityFeedVariants, activityItemVariants, agentChatPanelVariants, alertVariants, badgeVariants, breakpoints, buttonVariants, chatBubbleVariants, chatPanelVariants, cn, colors, containerVariants, dataTableVariants, dialogContentVariants, dialogOverlayVariants, formVariants, gridItemVariants, gridVariants, headingVariants, iconButtonVariants, iconVariants, inlineVariants, labelVariants, layout, listVariants, navItemVariants, notificationTrayVariants, orgLogoVariants, previewPanelVariants, previewToolbarVariants, projectCardVariants, provisioningViewVariants, radius, requestCardVariants, reviewPanelVariants, socialLoginButtonVariants, spacing, spinnerVariants, splitPaneVariants, stackVariants, statCardVariants, stepCircleVariants, stepIndicatorVariants, surfaceVariants, tabsListVariants, tabsTriggerVariants, terminalPanelVariants, textVariants, toolCallCardVariants, topBarVariants, transitions, useIsDesktop, useIsMobile, useMediaQuery, useSidebar, workflowStages, zIndex };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map